import { CURRENT_SCHEMA_VERSION, SCHEMA_MIGRATIONS, runSchemaMigrations } from './schema-migrations.js'
import { reconcileCoaDocumentLinks } from './coa-migration.js'
import { auditDataIntegrity, repairDataIntegrity } from './data-integrity.js'
import { ATOMIC_SYNC_FORMAT, ATOMIC_SYNC_KEY, ATOMIC_MANIFEST_FORMAT, recordKey as atomicRecordKey, mergeAtomicSnapshots, atomicSnapshotSummary, validateAtomicSnapshot } from './sync-safety.js'
// PeptideVault v6.5 — Storage, sync, backup

// ══════════════════════════════════════════════════════
// STORAGE
// ══════════════════════════════════════════════════════
let dataDir = ''
const SEP = () => (dataDir.includes('\\') ? '\\' : '/')
const DATA_FILE = () => dataDir ? dataDir + SEP() + 'peptide-vault.json' : null
const pwaIdbReady = () => !isElectron && typeof window !== 'undefined' && window._useIDB === true &&
  typeof window.idbGet === 'function' && typeof window.idbSet === 'function' &&
  typeof window.idbDelete === 'function' && typeof window.idbKeys === 'function'

async function initData() {
  if (isElectron && window.api) {
    // Load persisted settings first — this is how we find the right data folder
    try {
      const saved = await window.api.getSettings()
      if (saved) {
        S.settings.syncMode      = saved.syncMode      || 'auto'
        S.settings.customDir     = saved.customDir     || ''
        // Preserve secure cloud defaults when older settings files omit/blank these fields.
        // This keeps sync + Worker-backed AI usable after upgrades without writing API secrets locally.
        if (saved.cloudUrl)   S.settings.cloudUrl = saved.cloudUrl
        if (saved.cloudToken) S.settings.cloudToken = saved.cloudToken
        // Removed: anthropicKey no longer loaded from settings (safeStorage only)
        S.settings.autoSyncEnabled    = saved.autoSyncEnabled !== false
        S.settings.autoSyncIntervalMin = saved.autoSyncIntervalMin || 0
        if (saved._aiPrivacy && typeof saved._aiPrivacy === 'object') S._aiPrivacy = Object.assign({}, S._aiPrivacy || {}, saved._aiPrivacy)
        if (saved._densityMode === 'compact' || saved._densityMode === 'comfortable') S._densityMode = saved._densityMode
      }
    } catch(e) {}
    // Now get the data dir based on those settings
    dataDir = await window.api.getDataDir()
    S.settings.dataDir = dataDir
    const res = await window.api.readFile(DATA_FILE())
    let loadedOk = false
    if (res.ok) {
      try {
        const d = runSchemaMigrations(JSON.parse(res.data))
        S.peptides   = d.peptides   || []
        S.documents  = d.documents  || []
        S.storageLocations = d.storageLocations || []
        S.injections = d.injections || []
        S.healthData  = d.healthData  || []
        S.labResults  = d.labResults  || []
        S.supplements = d.supplements || []
        S.notes       = d.notes       || []
        S.users         = d.users         || []
        S.peptideLibrary= d.peptideLibrary|| []
        S.changeLog     = d.changeLog     || []
        S.customCategories = d.customCategories || []
        S.refillLog = d.refillLog || []
        S.injectionSupplies = d.injectionSupplies || []
        S.peptidePens = d.peptidePens || []
        S.vendors = d.vendors || []
        S.cycleTimelines = d.cycleTimelines || []
        S.blends = d.blends || []
        S.regimenVersions = d.regimenVersions || []
        S.coas = d.coas || []
        S.labMarkerDictionary = d.labMarkerDictionary || []
        S.savedViews = d.savedViews || []
        S.trash = d.trash || []
        S.snapshots = d.snapshots || []
        S.reportPresets = d.reportPresets || []
        S.masterPlanRules = d.masterPlanRules || []
        S.protocolDependencies = d.protocolDependencies || []
        S.vaultPositions = d.vaultPositions || []
        S.vaultAssignments = d.vaultAssignments || []
        S.vaultMovements = d.vaultMovements || []
        S.vaultReconciliations = d.vaultReconciliations || []
        S.protocolRequirements = d.protocolRequirements || []
        S.mobilePickingSheets = d.mobilePickingSheets || []
        S.supplementLabelReviews = d.supplementLabelReviews || []
        S.preparationBatches = d.preparationBatches || []
        if (d._schemaVersion) S._schemaVersion = d._schemaVersion
        S._syncRevisions = d._syncRevisions || {}
        S._syncBaseline = d._syncBaseline || null
        S._syncPendingOps = d._syncPendingOps || {}
        S._syncSnapshotRevision = d._syncSnapshotRevision || ''
        S._syncSnapshotBaseline = d._syncSnapshotBaseline || null
        S._syncTombstones = d._syncTombstones || {}
        S._syncDeviceId = d._syncDeviceId || S._syncDeviceId || uid()
        S._pendingLocalDataMigration = d._pendingLocalDataMigration || ''
        S.inventoryReservations = d.inventoryReservations || []
        S.scenarios = d.scenarios || []
        S.protocols = d.protocols || []
        if (d.emergencyContact) S.settings.emergencyContact = d.emergencyContact
        if (d.iceData) S.settings.iceData = d.iceData
        if (d.settings) {
          S.settings.lowStockThreshold = d.settings.lowStockThreshold
        }
        migrateLegacy()
        loadedOk = true
      } catch (e) { console.warn('parse error', e) }
    }
    // SAFETY: if the data file was missing or corrupt, try to recover from the most
    // recent local backup instead of starting empty (which could then overwrite cloud).
    if (!loadedOk || dataRecordCount() === 0) {
      const recovered = await tryLoadLatestBackup()
      if (recovered) {
        S._loadedFromBackup = true
      } else if (!res.ok) {
        // Nothing local at all — block auto-push until the user pulls or confirms,
        // so an empty first-run can never clobber a cloud backup.
        S._blockAutoPushUntilData = true
      }
    }
  } else {
    let browserLoadedOk = false

      // Load PWA settings from localStorage
      if (!isElectron) {
        try {
          const savedSettings = localStorage.getItem('pvault-settings')
          if (savedSettings) {
            const parsedSettings = JSON.parse(savedSettings)
            // Merge persisted UI settings. The endpoint may have a public default,
            // but the credential is always provisioned and stored per device.
            const savedCloudUrl = parsedSettings.cloudUrl
            const savedCloudToken = parsedSettings.cloudToken
            Object.assign(S.settings, parsedSettings)
            if (!savedCloudUrl) S.settings.cloudUrl = S.settings.cloudUrl || 'https://peptide-vault.pages.dev/api/data'
            if (!savedCloudToken) S.settings.cloudToken = ''
            if (parsedSettings._aiPrivacy && typeof parsedSettings._aiPrivacy === 'object') S._aiPrivacy = Object.assign({}, S._aiPrivacy || {}, parsedSettings._aiPrivacy)
            if (parsedSettings._densityMode === 'compact' || parsedSettings._densityMode === 'comfortable') S._densityMode = parsedSettings._densityMode
          }
        } catch(e) {}
      }
    let raw = pwaIdbReady() ? await window.idbGet('pvault') : localStorage.getItem('pvault')
    if (pwaIdbReady()) {
      const legacyRaw = localStorage.getItem('pvault')
      const recoveryKey = 'pvault-backup-legacy-storage-' + syncStringHash(legacyRaw || '')
      if (legacyRaw && !await window.idbGet(recoveryKey)) await window.idbSet(recoveryKey, legacyRaw)
    }
    if (!raw && pwaIdbReady()) {
      raw = localStorage.getItem('pvault')
      // Preserve the legacy copy until the full vault transaction commits.
      if (raw) await window.idbSet('pvault', raw)
    }
    if (raw) {
      try {
        const d = runSchemaMigrations(JSON.parse(raw))
        S.peptides  = d.peptides  || []
        S.documents = d.documents || []
        S.storageLocations = d.storageLocations || []
        S.injections = d.injections || []
        S.healthData  = d.healthData  || []
        S.labResults  = d.labResults  || []
        S.supplements = d.supplements || []
        S.notes       = d.notes       || []
        S.users         = d.users         || []
        S.peptideLibrary= d.peptideLibrary|| []
        S.changeLog     = d.changeLog     || []
        S.customCategories = d.customCategories || []
        S.refillLog = d.refillLog || []
        S.injectionSupplies = d.injectionSupplies || []
        S.peptidePens = d.peptidePens || []
        S.vendors = d.vendors || []
        S.cycleTimelines = d.cycleTimelines || []
        S.blends = d.blends || []
        S.regimenVersions = d.regimenVersions || []
        S.coas = d.coas || []
        S.labMarkerDictionary = d.labMarkerDictionary || []
        S.savedViews = d.savedViews || []
        S.trash = d.trash || []
        S.snapshots = d.snapshots || []
        S.reportPresets = d.reportPresets || []
        S.masterPlanRules = d.masterPlanRules || []
        S.protocolDependencies = d.protocolDependencies || []
        S.vaultPositions = d.vaultPositions || []
        S.vaultAssignments = d.vaultAssignments || []
        S.vaultMovements = d.vaultMovements || []
        S.vaultReconciliations = d.vaultReconciliations || []
        S.protocolRequirements = d.protocolRequirements || []
        S.mobilePickingSheets = d.mobilePickingSheets || []
        S.supplementLabelReviews = d.supplementLabelReviews || []
        S.preparationBatches = d.preparationBatches || []
        if (d._schemaVersion) S._schemaVersion = d._schemaVersion
        S._syncRevisions = d._syncRevisions || {}
        S._syncBaseline = d._syncBaseline || null
        S._syncPendingOps = d._syncPendingOps || {}
        S._syncSnapshotRevision = d._syncSnapshotRevision || ''
        S._syncSnapshotBaseline = d._syncSnapshotBaseline || null
        S._syncTombstones = d._syncTombstones || {}
        S._syncDeviceId = d._syncDeviceId || S._syncDeviceId || uid()
        S._pendingLocalDataMigration = d._pendingLocalDataMigration || ''
        S.inventoryReservations = d.inventoryReservations || []
        S.scenarios = d.scenarios || []
        S.protocols = d.protocols || []
        if (d.emergencyContact) S.settings.emergencyContact = d.emergencyContact
        if (d.iceData) S.settings.iceData = d.iceData
        if (d.settings) S.settings = { ...S.settings, ...d.settings }
        migrateLegacy()
        browserLoadedOk = true
      } catch (e) {
        console.warn('PWA data parse error:', e.message)
      }
    }
    // Match desktop recovery behavior. A corrupt browser database must never
    // silently render as a new empty vault or be allowed to overwrite cloud.
    if (!browserLoadedOk || dataRecordCount() === 0) {
      const recovered = await tryLoadLatestBackup()
      if (recovered) S._loadedFromBackup = true
      else S._blockAutoPushUntilData = true
    }
  }
  if (!S._syncBaseline) {
    S._syncBaseline = {}
    SYNC_KEYS.forEach(function(key) { S._syncBaseline[key] = cloneSyncValue(S[key] || []) })
  }
}

// Attempt to load the most recent local backup (used when the main data file is
// missing or corrupt, so the app never silently starts empty).
async function tryLoadLatestBackup() {
  try {
    let json = null
    if (isElectron && window.api && window.api.listFiles && dataDir) {
      const files = (await window.api.listFiles(`${dataDir}${SEP()}backups`)) || []
      const backups = files.filter(f => f.startsWith('pvault-backup-')).sort()
      if (!backups.length) return false
      const latest = backups[backups.length - 1]
      const res = await window.api.readFile(`${dataDir}${SEP()}backups${SEP()}${latest}`)
      if (res.ok) json = res.data
    } else {
      const keys = pwaIdbReady() ? (await window.idbKeys('pvault-backup-')).sort() : Object.keys(localStorage).filter(k => k.startsWith('pvault-backup-')).sort()
      if (!keys.length) return false
      json = pwaIdbReady() ? await window.idbGet(keys[keys.length - 1]) : localStorage.getItem(keys[keys.length - 1])
    }
    if (!json) return false
    const d = runSchemaMigrations(JSON.parse(json))
    if (dataRecordCount(d) === 0) return false
    for (const k of ['peptides','documents','storageLocations','injections','healthData','notes','labResults','supplements','users','peptideLibrary','changeLog','customCategories','refillLog','injectionSupplies','peptidePens','vendors','cycleTimelines','blends','protocols','scenarios','inventoryReservations','regimenVersions','coas','labMarkerDictionary','savedViews','trash','snapshots','reportPresets','masterPlanRules','protocolDependencies','vaultPositions','vaultAssignments','vaultMovements','vaultReconciliations','protocolRequirements','mobilePickingSheets','supplementLabelReviews','preparationBatches']) {
      S[k] = d[k] || []
    }
    migrateLegacy()
    console.warn('Recovered data from local backup.')
    return true
  } catch (e) {
    console.warn('tryLoadLatestBackup failed:', e.message)
    return false
  }
}

// Write a timestamped local backup snapshot. Keeps a rolling set so there's always
// a recent restore point before any destructive operation (pull, clear, import).
async function writeLocalBackup(reason) {
  try {
    const payload = { peptides: S.peptides, documents: S.documents, storageLocations: S.storageLocations, injections: S.injections, healthData: S.healthData, notes: S.notes, labResults: S.labResults, supplements: S.supplements, users: S.users, peptideLibrary: S.peptideLibrary, changeLog: S.changeLog, customCategories: S.customCategories, refillLog: S.refillLog, injectionSupplies: S.injectionSupplies, peptidePens: S.peptidePens, vendors: S.vendors, cycleTimelines: S.cycleTimelines, blends: S.blends, protocols: S.protocols||[], scenarios: S.scenarios||[], inventoryReservations: S.inventoryReservations||[], regimenVersions: S.regimenVersions||[], coas: S.coas||[], labMarkerDictionary: S.labMarkerDictionary||[], savedViews: S.savedViews||[], trash: S.trash||[], snapshots: S.snapshots||[], reportPresets: S.reportPresets||[], masterPlanRules: S.masterPlanRules||[], protocolDependencies: S.protocolDependencies||[], vaultPositions:S.vaultPositions||[], vaultAssignments:S.vaultAssignments||[], vaultMovements:S.vaultMovements||[], vaultReconciliations:S.vaultReconciliations||[], protocolRequirements:S.protocolRequirements||[], mobilePickingSheets:S.mobilePickingSheets||[], supplementLabelReviews:S.supplementLabelReviews||[], preparationBatches:S.preparationBatches||[], _schemaVersion: S._schemaVersion||1, _backupReason: reason||'manual', _backupAt: now() }
    const count = dataRecordCount(payload)
    if (count === 0) return   // never save an empty backup over good ones
    const stamp = new Date().toISOString().replace(/[:.]/g,'-').slice(0,19)
    const json = JSON.stringify(payload)
    if (isElectron && window.api && window.api.writeFile && dataDir) {
      // Desktop: write to a backups folder, keep the most recent 10
      const name = `backups${SEP()}pvault-backup-${stamp}.json`
      await window.api.writeFile(`${dataDir}${SEP()}${name}`, json)
      if (window.api.listFiles) {
        try {
          const files = (await window.api.listFiles(`${dataDir}${SEP()}backups`)) || []
          const backups = files.filter(f => f.startsWith('pvault-backup-')).sort()
          // delete oldest beyond the 10 most recent
          for (const old of backups.slice(0, Math.max(0, backups.length - 10))) {
            if (window.api.deleteFile) await window.api.deleteFile(`${dataDir}${SEP()}backups${SEP()}${old}`)
          }
        } catch (e) { /* listing/cleanup is best-effort */ }
      }
    } else {
      // PWA/browser: keep the last 3 in localStorage (space-limited)
      try {
        const key = `pvault-backup-${stamp}`
        if (pwaIdbReady()) {
          await window.idbSet(key, json)
          const allKeys = (await window.idbKeys('pvault-backup-')).sort()
          for (const old of allKeys.slice(0, Math.max(0, allKeys.length - 5))) await window.idbDelete(old)
        } else {
          localStorage.setItem(key, json)
          const allKeys = Object.keys(localStorage).filter(k => k.startsWith('pvault-backup-')).sort()
          for (const old of allKeys.slice(0, Math.max(0, allKeys.length - 3))) localStorage.removeItem(old)
        }
      } catch (e) { /* localStorage may be full — non-fatal */ }
    }
    S._lastBackupAt = new Date().toISOString()
  } catch (e) {
    console.warn('writeLocalBackup failed:', e.message)
  }
}

async function manualBackupNow() {
  if (dataRecordCount() === 0) { showToast('Nothing to back up'); return }
  await writeLocalBackup('manual')
  showToast('✅ Backup saved')
}

async function restoreLatestBackup() {
  if (!await asyncConfirm('Restore the most recent local backup?\n\nThis replaces current data with the latest snapshot. A backup is taken first.')) return
  if (dataRecordCount() > 0) await writeLocalBackup('before-restore')
  const ok = await tryLoadLatestBackup()
  if (ok) { await save(); render(); showToast('✅ Restored from latest backup') }
  else showToast('No local backup found')
}

// Count total meaningful records — used to guard against pushing/saving a collapsed dataset
function dataRecordCount(obj) {
  const o = obj || S
  const keys = ['peptides','documents','storageLocations','injections','healthData','notes','labResults','supplements','users','peptideLibrary','changeLog','customCategories','refillLog','injectionSupplies','peptidePens','vendors','cycleTimelines','blends','protocols','scenarios','inventoryReservations','regimenVersions','coas','labMarkerDictionary','savedViews','trash','snapshots','reportPresets','masterPlanRules','protocolDependencies','vaultPositions','vaultAssignments','vaultMovements','vaultReconciliations','protocolRequirements','mobilePickingSheets','supplementLabelReviews','preparationBatches']
  return keys.reduce((sum, k) => sum + ((o[k]||[]).length), 0)
}

let _lastSaveTime = 0
let _localDirty = false     // true when save() was called but push hasn't completed yet
let _lastSyncError = null   // { at, operation, message } — runtime-only, never synced
let _lastSyncAttempt = null
let _syncProgress = null
let _syncDomainStatus = {}
let _syncConflicts = {}
let _syncConflictDetails = {}
let _pendingChangeCount = 0
let _localMutationVersion = 0
function syncDeviceId() { if (!S._syncDeviceId) S._syncDeviceId = uid(); return S._syncDeviceId }

function setSyncProgress(message, phase) {
  _syncProgress = message ? { message, phase:phase || 'working', at:new Date().toISOString() } : null
  renderSyncIndicator()
}

function markAtomicDomains(operation, revision, sourceDevice, message) {
  const at = new Date().toISOString()
  SYNC_KEYS.forEach(function(key) {
    _syncDomainStatus[key] = {operation:operation,state:'success',at:at,revision:revision || '',sourceDevice:sourceDevice || '',message:message || ''}
  })
}

function hasPendingSyncOperations() {
  return Object.values(S._syncPendingOps || {}).some(function(ops) { return Array.isArray(ops) && ops.length > 0 })
}

function currentDataPayload() {
  return { peptides: S.peptides, documents: S.documents, storageLocations: S.storageLocations, injections: S.injections, healthData: S.healthData, notes: S.notes, labResults: S.labResults, supplements: S.supplements, users: S.users, peptideLibrary: S.peptideLibrary, changeLog: S.changeLog, customCategories: S.customCategories, refillLog: S.refillLog, injectionSupplies: S.injectionSupplies, peptidePens: S.peptidePens, vendors: S.vendors, cycleTimelines: S.cycleTimelines, blends: S.blends, protocols: S.protocols||[], scenarios: S.scenarios||[], inventoryReservations: S.inventoryReservations||[], regimenVersions: S.regimenVersions||[], coas: S.coas||[], labMarkerDictionary: S.labMarkerDictionary||[], savedViews: S.savedViews||[], trash: S.trash||[], snapshots: S.snapshots||[], reportPresets: S.reportPresets||[], masterPlanRules: S.masterPlanRules||[], protocolDependencies: S.protocolDependencies||[], vaultPositions:S.vaultPositions||[], vaultAssignments:S.vaultAssignments||[], vaultMovements:S.vaultMovements||[], vaultReconciliations:S.vaultReconciliations||[], protocolRequirements:S.protocolRequirements||[], mobilePickingSheets:S.mobilePickingSheets||[], supplementLabelReviews:S.supplementLabelReviews||[], preparationBatches:S.preparationBatches||[], _schemaVersion: S._schemaVersion||1, _syncRevisions: S._syncRevisions||{}, _syncBaseline:S._syncBaseline||{}, _syncPendingOps:S._syncPendingOps||{}, _syncSnapshotRevision:S._syncSnapshotRevision||'', _syncSnapshotBaseline:S._syncSnapshotBaseline||null, _syncTombstones:S._syncTombstones||{}, _syncDeviceId: syncDeviceId(), emergencyContact: S.settings.emergencyContact||{}, iceData: S.settings.iceData||{} }
}

function cloneSyncValue(value) { return JSON.parse(JSON.stringify(value == null ? [] : value)) }
function syncRecordKey(record, index) {
  if (record && typeof record === 'object') {
    if (record.id != null) return 'id:' + record.id
    if (record.label != null) return 'label:' + record.label
    if (record.name != null) return 'name:' + record.name
  }
  return 'value:' + JSON.stringify(record) + ':' + index
}
function syncMap(records) {
  var map = {}
  ;(records || []).forEach(function(record, index) { map[syncRecordKey(record, index)] = record })
  return map
}
function captureSyncOperations() {
  if (!S._syncBaseline) {
    S._syncBaseline = {}
    SYNC_KEYS.forEach(function(key) { S._syncBaseline[key] = cloneSyncValue(S[key] || []) })
  }
  S._syncPendingOps = {}
  if (!S._syncTombstones) S._syncTombstones = {}
  SYNC_KEYS.forEach(function(key) {
    var base = syncMap(S._syncBaseline[key] || []), current = syncMap(S[key] || []), ops = []
    if (!S._syncTombstones[key]) S._syncTombstones[key] = {}
    Object.keys(base).forEach(function(recordKey) {
      if (!(recordKey in current)) {
        var deletedAt = S._syncTombstones[key][recordKey] || new Date().toISOString()
        S._syncTombstones[key][recordKey] = deletedAt
        ops.push({ type:'delete', key:recordKey, base:base[recordKey], at:deletedAt })
      }
    })
    Object.keys(current).forEach(function(recordKey) {
      delete S._syncTombstones[key][recordKey]
      if (!(recordKey in base) || JSON.stringify(base[recordKey]) !== JSON.stringify(current[recordKey])) {
        ops.push({ type:'upsert', key:recordKey, base:base[recordKey], value:current[recordKey], at:new Date().toISOString() })
      }
    })
    if (ops.length) S._syncPendingOps[key] = ops
  })
}

function buildAtomicSnapshot() {
  var domains = {}
  SYNC_KEYS.forEach(function(key) { domains[key] = cloneSyncValue(S[key] || []) })
  return {
    format:ATOMIC_SYNC_FORMAT,
    schemaVersion:S._schemaVersion || 1,
    generationId:syncDeviceId() + ':' + new Date().toISOString(),
    clientCommittedAt:new Date().toISOString(),
    sourceDevice:syncDeviceId(),
    domains:domains,
    settingsData:{
      emergencyContact:S.settings.emergencyContact || {},
      iceData:S.settings.iceData || {},
      environmentSensor:{
        sku:String(S.settings.goveeDeviceSku || ''),
        deviceId:String(S.settings.goveeDeviceId || ''),
        name:String(S.settings.goveeDeviceName || '')
      }
    },
    tombstones:cloneSyncValue(S._syncTombstones || {})
  }
}

function applyAtomicSnapshot(snapshot, revision) {
  SYNC_KEYS.forEach(function(key) { S[key] = cloneSyncValue(snapshot.domains[key] || []) })
  if (snapshot.settingsData) {
    S.settings.emergencyContact = snapshot.settingsData.emergencyContact || {}
    S.settings.iceData = snapshot.settingsData.iceData || {}
    // Sensor identity is safe to sync; the Govee API key remains a Worker secret.
    // Preserve a device's existing selection when reading an older snapshot.
    if (snapshot.settingsData.environmentSensor) {
      S.settings.goveeDeviceSku = String(snapshot.settingsData.environmentSensor.sku || '')
      S.settings.goveeDeviceId = String(snapshot.settingsData.environmentSensor.deviceId || '')
      S.settings.goveeDeviceName = String(snapshot.settingsData.environmentSensor.name || '')
    }
  }
  S._syncTombstones = cloneSyncValue(snapshot.tombstones || {})
  S._syncSnapshotRevision = revision || S._syncSnapshotRevision || ''
  // Domain baselines already live in _syncBaseline. Keep only atomic metadata
  // here so the local data file is not doubled a second time.
  S._syncSnapshotBaseline = {
    format:snapshot.format,
    schemaVersion:snapshot.schemaVersion,
    settingsData:cloneSyncValue(snapshot.settingsData || {}),
    tombstones:cloneSyncValue(snapshot.tombstones || {}),
    domainParts:cloneSyncValue(snapshot._manifest && snapshot._manifest.domainParts || {})
  }
  S._syncBaseline = {}
  SYNC_KEYS.forEach(function(key) { S._syncBaseline[key] = cloneSyncValue(S[key] || []) })
  S._syncPendingOps = {}
}

async function fetchAtomicSnapshot() {
  const url = cloudUrlForKey(ATOMIC_SYNC_KEY)
  if (!url) return undefined
  for (let attempt=1; attempt<=5; attempt++) {
    try {
      const response = await fetch(url, { headers:{'X-Token':S.settings.cloudToken,'X-Device-ID':syncDeviceId(),'X-Sync-Version':'2'} })
      if ([429,500,502,503].includes(response.status) && attempt<5) {
        const retryAfter = Number(response.headers.get('Retry-After') || 0)
        await new Promise(resolve=>setTimeout(resolve,retryAfter > 0 ? Math.min(retryAfter*1000,65000) : attempt*1200)); continue
      }
      const body = await response.json()
      if (!response.ok) throw new Error(body.error || ('HTTP '+response.status))
      if (!body.data) return null
      const revision=response.headers.get('ETag') || body.updatedAt || ''
      if (body.data.format===ATOMIC_SYNC_FORMAT) return {snapshot:body.data,revision:revision,serverCommittedAt:body.data.serverCommittedAt||body.updatedAt||'',sourceDevice:body.sourceDevice||''}
      if (body.data.format!==ATOMIC_MANIFEST_FORMAT || !body.data.domainParts) throw new Error('Cloud commit manifest is invalid')
      const manifest=body.data, domains={}
      for (const key of SYNC_KEYS) {
        const descriptor=manifest.domainParts[key]
        if (!descriptor || !Array.isArray(descriptor.parts)) throw new Error('Cloud commit is missing '+key)
        const chunks=[]
        for (const part of descriptor.parts) {
          const chunk=await fetchAtomicPart(part.key)
          if (typeof chunk!=='string') throw new Error('Cloud commit part is missing: '+part.key)
          chunks.push(chunk)
        }
        const serialized=chunks.join('')
        if (syncStringHash(serialized)!==descriptor.hash) throw new Error('Cloud commit integrity check failed for '+key)
        const records=JSON.parse(serialized)
        if (!Array.isArray(records)) throw new Error('Cloud commit domain is invalid: '+key)
        domains[key]=records
      }
      const snapshot={format:ATOMIC_SYNC_FORMAT,schemaVersion:manifest.schemaVersion,domains:domains,settingsData:manifest.settingsData||{},tombstones:manifest.tombstones||{},serverCommittedAt:manifest.serverCommittedAt||body.updatedAt||'',clientCommittedAt:manifest.clientCommittedAt||'',sourceDevice:manifest.sourceDevice||'',generationId:manifest.generationId||''}
      snapshot._manifest=manifest
      return {snapshot:snapshot,revision:revision,serverCommittedAt:snapshot.serverCommittedAt,sourceDevice:body.sourceDevice||manifest.sourceDevice||''}
    } catch (error) {
      if (attempt<5) { await new Promise(resolve=>setTimeout(resolve,attempt*1200)); continue }
      _lastSyncError={at:new Date().toISOString(),operation:'atomic-pull',message:error.message||'Atomic snapshot pull failed'}
      return undefined
    }
  }
}

function syncStringHash(text) {
  var hash=2166136261
  for (var i=0;i<text.length;i++){hash^=text.charCodeAt(i);hash=Math.imul(hash,16777619)}
  return ('00000000'+(hash>>>0).toString(16)).slice(-8)
}

function syncStableJson(value) {
  if (Array.isArray(value)) return '[' + value.map(syncStableJson).join(',') + ']'
  if (value && typeof value === 'object') return '{' + Object.keys(value).sort().map(function(key) { return JSON.stringify(key) + ':' + syncStableJson(value[key]) }).join(',') + '}'
  return JSON.stringify(value)
}

function canonicalSyncRecords(records) {
  return cloneSyncValue(records || []).sort(function(left, right) {
    return syncRecordKey(left, 0).localeCompare(syncRecordKey(right, 0))
  })
}

function atomicSnapshotContentEqual(left, right) {
  if (!left || !right) return false
  if (Number(left.schemaVersion || 0) !== Number(right.schemaVersion || 0)) return false
  if (!SYNC_KEYS.every(function(key) { return JSON.stringify(canonicalSyncRecords(left.domains && left.domains[key])) === JSON.stringify(canonicalSyncRecords(right.domains && right.domains[key])) })) return false
  return syncStableJson(left.settingsData || {}) === syncStableJson(right.settingsData || {}) && syncStableJson(left.tombstones || {}) === syncStableJson(right.tombstones || {})
}

function atomicStringParts(text, maxChars) {
  var parts=[], size=maxChars || 350000
  for (var i=0;i<text.length;i+=size) parts.push(text.slice(i,i+size))
  return parts.length ? parts : ['[]']
}

async function fetchAtomicPart(key) {
  for (let attempt=1;attempt<=4;attempt++) {
    const response=await fetch(cloudUrlForKey(key),{headers:{'X-Token':S.settings.cloudToken,'X-Device-ID':syncDeviceId(),'X-Sync-Version':'2'}})
    if (response.ok) { const body=await response.json(); return body.data }
    if ([429,500,502,503].includes(response.status) && attempt<4) {
      const retryAfter=Number(response.headers.get('Retry-After')||0)
      await new Promise(resolve=>setTimeout(resolve,retryAfter>0?Math.min(retryAfter*1000,65000):attempt*1200)); continue
    }
    return undefined
  }
}

async function ensureAtomicPart(key, value) {
  const headers={'Content-Type':'application/json','X-Token':S.settings.cloudToken,'X-Device-ID':syncDeviceId(),'X-Sync-Version':'2','If-None-Match':'*'}
  for (let attempt=1;attempt<=4;attempt++) {
    try {
      const response=await fetch(cloudUrlForKey(key),{method:'POST',headers:headers,body:JSON.stringify({data:value})})
      if (response.ok) return true
      if (response.status===412) return (await fetchAtomicPart(key))===value
      if ([429,500,502,503].includes(response.status) && attempt<4) {
        const retryAfter=Number(response.headers.get('Retry-After')||0)
        await new Promise(resolve=>setTimeout(resolve,retryAfter>0?Math.min(retryAfter*1000,65000):attempt*1200)); continue
      }
      return false
    } catch(error) {
      if (attempt<4) { await new Promise(resolve=>setTimeout(resolve,attempt*1000)); continue }
    }
  }
  return false
}

async function atomicManifestForSnapshot(snapshot) {
  const domainParts={}
  const priorParts=S._syncSnapshotBaseline && S._syncSnapshotBaseline.domainParts || {}
  for (const key of SYNC_KEYS) {
    setSyncProgress('Uploading ' + key + '…', 'uploading')
    // Top-level record order is presentation detail, not data. Canonicalizing
    // it prevents otherwise identical devices from alternating cloud revisions.
    const serialized=JSON.stringify(canonicalSyncRecords(snapshot.domains[key] || []))
    const hash=syncStringHash(serialized)
    if (priorParts[key] && priorParts[key].hash===hash) { domainParts[key]=cloneSyncValue(priorParts[key]); continue }
    const chunks=atomicStringParts(serialized)
    const parts=[]
    for (let index=0;index<chunks.length;index++) {
      const partKey='syncv2_'+key+'_'+hash+'_'+index
      if (!await ensureAtomicPart(partKey,chunks[index])) throw new Error('Could not upload committed part '+key+' '+(index+1)+'/'+chunks.length)
      parts.push({key:partKey,index:index})
    }
    domainParts[key]={hash:hash,count:(snapshot.domains[key]||[]).length,parts:parts}
  }
  return {format:ATOMIC_MANIFEST_FORMAT,schemaVersion:snapshot.schemaVersion,generationId:snapshot.generationId,clientCommittedAt:snapshot.clientCommittedAt,sourceDevice:snapshot.sourceDevice,settingsData:snapshot.settingsData||{},tombstones:snapshot.tombstones||{},domainParts:domainParts}
}

async function pushAtomicSnapshotOnce(forceReconcile = false) {
  const url = cloudUrlForKey(ATOMIC_SYNC_KEY)
  if (!url) return false
  var pending = buildAtomicSnapshot()
  var started = cloneSyncValue(pending)
  var mutationVersion = _localMutationVersion
  var revision = forceReconcile ? '' : (S._syncSnapshotRevision || '')
  setSyncProgress('Preparing cloud update…', 'preparing')
  const trustedBaseline = S._syncBaseline && SYNC_KEYS.every(function(key) { return Array.isArray(S._syncBaseline[key]) })
    ? {schemaVersion:pending.schemaVersion,domains:S._syncBaseline,settingsData:S._syncSnapshotBaseline && S._syncSnapshotBaseline.settingsData || {},tombstones:S._syncSnapshotBaseline && S._syncSnapshotBaseline.tombstones || {}}
    : null
  if (revision && trustedBaseline && atomicSnapshotContentEqual(pending,trustedBaseline)) {
    _syncDomainStatus.atomic={operation:'push',state:'success',at:new Date().toISOString(),revision:revision,sourceDevice:syncDeviceId(),message:'No cloud changes needed'}
    markAtomicDomains('current',revision,syncDeviceId(),'No cloud changes needed')
    return true
  }
  for (let attempt=1; attempt<=5; attempt++) {
    const headers={'Content-Type':'application/json','X-Token':S.settings.cloudToken,'X-Device-ID':syncDeviceId(),'X-Sync-Version':'2'}
    if (revision) headers['If-Match']=revision
    else headers['If-None-Match']='*'
    try {
      const manifest=await atomicManifestForSnapshot(pending)
      setSyncProgress('Committing all changes…', 'committing')
      const response=await fetch(url,{method:'POST',headers:headers,body:JSON.stringify({data:manifest})})
      if (response.ok) {
        const body=await response.json()
        const newRevision=response.headers.get('ETag') || body.updatedAt || ''
        pending.serverCommittedAt=body.updatedAt || new Date().toISOString()
        pending._manifest=Object.assign({},manifest,{serverCommittedAt:pending.serverCommittedAt})
        if (_localMutationVersion === mutationVersion) {
          applyAtomicSnapshot(pending,newRevision)
        } else {
          // A user edit landed while the request was in flight. Merge it with
          // the committed result, but keep the committed snapshot as baseline
          // so the new edit remains pending for the serialized follow-up push.
          const current = buildAtomicSnapshot()
          const merged = mergeAtomicSnapshots(started,current,pending,SYNC_KEYS,current.clientCommittedAt,pending.serverCommittedAt)
          SYNC_KEYS.forEach(function(key) { S[key] = cloneSyncValue(merged.domains[key] || []) })
          S._syncTombstones = cloneSyncValue(merged.tombstones || {})
          S._syncSnapshotRevision = newRevision
          S._syncSnapshotBaseline = {format:pending.format,schemaVersion:pending.schemaVersion,settingsData:cloneSyncValue(pending.settingsData||{}),tombstones:cloneSyncValue(pending.tombstones||{}),domainParts:cloneSyncValue(manifest.domainParts||{})}
          S._syncBaseline = {}
          SYNC_KEYS.forEach(function(key) { S._syncBaseline[key] = cloneSyncValue(pending.domains[key] || []) })
          S._syncPendingOps = {}
          captureSyncOperations()
        }
        _syncDomainStatus.atomic={operation:'push',state:'success',at:new Date().toISOString(),revision:newRevision,sourceDevice:syncDeviceId(),message:'All domains committed together'}
        markAtomicDomains('push',newRevision,syncDeviceId(),'All domains committed together')
        await writeCurrentStateLocalOnly()
        return true
      }
      if (response.status===409 || response.status===412 || response.status===428) {
        setSyncProgress('Reconciling a newer cloud revision…', 'reconciling')
        const cloud=await fetchAtomicSnapshot()
        if (!cloud || !cloud.snapshot) return false
        const localChangedAt=pending.clientCommittedAt || new Date().toISOString()
        var baseline=Object.assign({},S._syncSnapshotBaseline || {},{domains:S._syncBaseline || {}})
        pending=mergeAtomicSnapshots(baseline,pending,cloud.snapshot,SYNC_KEYS,localChangedAt,cloud.serverCommittedAt || cloud.snapshot.clientCommittedAt)
        if (atomicSnapshotContentEqual(pending,cloud.snapshot)) {
          applyAtomicSnapshot(cloud.snapshot,cloud.revision)
          _syncDomainStatus.atomic={operation:'push',state:'success',at:new Date().toISOString(),revision:cloud.revision,sourceDevice:cloud.sourceDevice || '',message:'Already current after reconciliation'}
          markAtomicDomains('current',cloud.revision,cloud.sourceDevice || '','Already current after reconciliation')
          await writeCurrentStateLocalOnly()
          return true
        }
        pending.generationId=syncDeviceId()+':'+new Date().toISOString()
        pending.clientCommittedAt=new Date().toISOString()
        pending.sourceDevice=syncDeviceId()
        const validation=validateAtomicSnapshot(pending,currentDomainsObject(),SYNC_KEYS)
        if (!validation.ok) throw new Error(validation.errors.join('; '))
        revision=cloud.revision
        S._lastSyncPreview=validation.summary
        continue
      }
      if ([429,500,502,503].includes(response.status) && attempt<5) { await new Promise(resolve=>setTimeout(resolve,attempt*1200)); continue }
      var errorBody={}; try{errorBody=await response.json()}catch(e){}
      throw new Error(errorBody.error || ('HTTP '+response.status))
    } catch(error) {
      if (attempt<5 && /network|fetch/i.test(error.message||'')) { await new Promise(resolve=>setTimeout(resolve,attempt*1200)); continue }
      _lastSyncError={at:new Date().toISOString(),operation:'atomic-push',message:error.message||'Atomic snapshot push failed'}
      return false
    }
  }
  _lastSyncError={at:new Date().toISOString(),operation:'atomic-push',message:'Cloud revision changed repeatedly; the update remains queued for automatic retry'}
  return false
}

let _atomicPushChain = Promise.resolve()
function pushAtomicSnapshot(forceReconcile = false) {
  const run = function() { return pushAtomicSnapshotOnce(forceReconcile) }
  const queued = _atomicPushChain.then(run,run).then(function(success) {
    // A failed retry sequence must not leave the sidebar claiming a sync is
    // still in progress indefinitely. The retained error explains the retry.
    if (!success) setSyncProgress(null)
    return success
  })
  _atomicPushChain = queued.catch(function() { return false })
  return queued
}

function currentDomainsObject() {
  var domains={}
  SYNC_KEYS.forEach(function(key){domains[key]=S[key]||[]})
  return domains
}

async function applyCloudSnapshotSafely(cloud, reason, options = {}) {
  if (!cloud || !cloud.snapshot) return false
  const actualCurrent=currentDomainsObject()
  // A device with no trusted local database must be allowed to adopt the
  // committed cloud snapshot. Placeholder/seed records are not authoritative.
  const current=options.trustCloud ? {} : actualCurrent
  const validation=validateAtomicSnapshot(cloud.snapshot,current,SYNC_KEYS)
  S._lastSyncPreview=validation.summary
  if (!validation.ok) {
    if (!options.trustCloud && validation.errors.every(error => error.startsWith('Cloud removal has no deletion record: '))) {
      // Reconcile through compare-and-swap before adopting anything. An empty
      // expected revision forces a fresh fetch of the committed cloud copy;
      // mergeDomain retains records without tombstones on either device.
      await writeLocalBackup('before-atomic-missing-record-reconciliation')
      setSyncProgress('Reconciling records missing deletion history…', 'reconciling')
      const reconciled = await pushAtomicSnapshot(true)
      if (reconciled) { _lastSyncError=null; return true }
      _lastSyncError={at:new Date().toISOString(),operation:'atomic-pull',message:(_lastSyncError && _lastSyncError.message) || 'Missing-record reconciliation remains pending'}
      return false
    }
    _lastSyncError={at:new Date().toISOString(),operation:'atomic-pull',message:'Cloud snapshot blocked: '+validation.errors.join('; ')}
    if (typeof debugLogEvent==='function') debugLogEvent('error','SYNC_REGRESSION_BLOCKED',_lastSyncError.message)
    return false
  }
  const changed=JSON.stringify(actualCurrent)!==JSON.stringify(cloud.snapshot.domains)
  if (changed) await writeLocalBackup('before-atomic-'+(reason||'pull'))
  applyAtomicSnapshot(cloud.snapshot,cloud.revision)
  markAtomicDomains('pull',cloud.revision,cloud.sourceDevice || cloud.snapshot.sourceDevice || '','Committed snapshot verified')
  if (options.trustCloud) S._blockAutoPushUntilData = false
  migrateLegacy()
  repairDataIntegrity(S)
  await writeCurrentStateLocalOnly()
  return changed
}

function threeWayRecord(base, local, remote, operationAt) {
  if (!base || !remote || typeof local !== 'object' || typeof remote !== 'object') return local
  var merged = Object.assign({}, remote)
  var localTime = Date.parse(local.updatedAt || local.createdAt || operationAt || 0) || 0
  var remoteTime = Date.parse(remote.updatedAt || remote.createdAt || 0) || 0
  Object.keys(local).forEach(function(field) {
    var baseValue = JSON.stringify(base[field]), localValue = JSON.stringify(local[field]), remoteValue = JSON.stringify(remote[field])
    var localChanged = localValue !== baseValue, remoteChanged = remoteValue !== baseValue
    if (localChanged && (!remoteChanged || localTime >= remoteTime)) merged[field] = local[field]
  })
  return merged
}

function pendingOpsForPiece(key) {
  if (SYNC_KEYS.includes(key)) return (S._syncPendingOps && S._syncPendingOps[key]) || []
  var match = key.match(/^(healthData|injections)_(\d{4})$/)
  if (!match) return []
  return ((S._syncPendingOps && S._syncPendingOps[match[1]]) || []).filter(function(op) {
    var record = op.value || op.base || {}
    return String(record.date || record.dateTime || '').slice(0,4) === match[2]
  })
}

function replayPendingOperations(key, cloudRecords) {
  var records = cloneSyncValue(cloudRecords || []), map = syncMap(records)
  pendingOpsForPiece(key).forEach(function(op) {
    if (op.type === 'delete') {
      var remote = map[op.key]
      var remoteChanged = remote && JSON.stringify(remote) !== JSON.stringify(op.base)
      var remoteTime = remote && (Date.parse(remote.updatedAt || remote.createdAt || 0) || 0)
      if (!remoteChanged || (Date.parse(op.at) || 0) >= remoteTime) delete map[op.key]
    } else {
      map[op.key] = map[op.key] ? threeWayRecord(op.base, op.value, map[op.key], op.at) : op.value
    }
  })
  return Object.keys(map).map(function(recordKey) { return map[recordKey] })
}

function markPieceSynced(key, records) {
  if (!S._syncBaseline) S._syncBaseline = {}
  if (SYNC_KEYS.includes(key)) {
    S._syncBaseline[key] = cloneSyncValue(records)
    if (S._syncPendingOps) delete S._syncPendingOps[key]
    return
  }
  var match = key.match(/^(healthData|injections)_(\d{4})$/)
  if (!match) return
  var domain = match[1], year = match[2]
  S._syncBaseline[domain] = (S._syncBaseline[domain] || []).filter(function(record) { return String(record.date || record.dateTime || '').slice(0,4) !== year }).concat(cloneSyncValue(records))
  if (S._syncPendingOps && S._syncPendingOps[domain]) {
    S._syncPendingOps[domain] = S._syncPendingOps[domain].filter(function(op) { var record=op.value||op.base||{}; return String(record.date||record.dateTime||'').slice(0,4) !== year })
    if (!S._syncPendingOps[domain].length) delete S._syncPendingOps[domain]
  }
}

// Persist an authoritative cloud copy on this device without manufacturing a
// local edit or scheduling a push back to the server.
async function writeCurrentStateLocalOnly() {
  const data = JSON.stringify(currentDataPayload(), null, 2)
  if (isElectron && window.api && DATA_FILE()) await window.api.writeFile(DATA_FILE(), data)
  else if (pwaIdbReady()) await window.idbSet('pvault', data)
  else localStorage.setItem('pvault', data)
}

async function save() {
  // Normalize safe relationships before persistence/sync. Physical vault-map
  // mismatches are intentionally never guessed or silently discarded.
  repairDataIntegrity(S)
  _lastSaveTime = Date.now()
  _localDirty = true
  _pendingChangeCount++
  _localMutationVersion++
  captureSyncOperations()
  const payload = currentDataPayload()
  const data = JSON.stringify(payload, null, 2)
  if (isElectron && window.api && DATA_FILE()) {
    await window.api.writeFile(DATA_FILE(), data)
  } else {
    try {
      if (pwaIdbReady()) { await window.idbSet('pvault', data) }
      else { localStorage.setItem('pvault', data) }
    } catch (e) {
      console.error('Vault persistence failed:', e)
      showToast('Could not save the complete vault on this device. Keep this window open and export a backup.','error')
      throw e
    }
  }
  // SAFETY: never AUTO-push an empty/collapsed dataset to the cloud — this would overwrite
  // a good cloud backup with nothing. Only auto-push when there's real data, or when the
  // user has explicitly allowed an empty push (e.g. right after intentionally clearing all data).
  if (S.settings.cloudUrl && S.settings.cloudToken && S.settings.autoSyncEnabled) {
    const count = dataRecordCount()
    if (S._blockAutoPushUntilData && count > 0) S._blockAutoPushUntilData = false
    if ((count > 0 && !S._blockAutoPushUntilData) || S._allowEmptyPush) {
      debouncedAutoPush()
    } else {
      console.warn('Auto-push skipped: empty/uninitialized dataset (guard against overwriting cloud backup).')
    }
  }
  if (typeof syncNotificationSnapshotSoon === 'function') syncNotificationSnapshotSoon()
}

async function saveSettings() {
  // P0-1: Never persist anthropicKey to disk/localStorage on Electron (safeStorage handles it)
  var settingsCopy = Object.assign({}, S.settings)
  // P1-1+P2-1: Persist local UI preferences alongside settings
  settingsCopy._aiPrivacy = S._aiPrivacy || {}
  settingsCopy._densityMode = S._densityMode || 'comfortable'
  if (typeof isElectron !== 'undefined' && isElectron) delete settingsCopy.anthropicKey
  if (isElectron && window.api && window.api.saveSettings) {
    await window.api.saveSettings(settingsCopy)
  } else {
    // PWA: also strip — PWA should never hold the Anthropic key
    delete settingsCopy.anthropicKey
    try { localStorage.setItem('pvault-settings', JSON.stringify(settingsCopy)) }
    catch(e) { console.warn('Settings save error:', e) }
  }
}


// ── Cloud sync (desktop) ──────────────────────────────
async function cloudPush(payload) {
  const url = normalizeCloudUrl(S.settings.cloudUrl)
  const body = JSON.stringify({ data: payload })
  // Retry on transient server errors (503/502/500/429) and network blips — up to 4 attempts
  for (let attempt = 1; attempt <= 4; attempt++) {
    try {
      const r = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Token': S.settings.cloudToken },
        body
      })
      let resBody = null
      try { resBody = await r.json() } catch (e) { /* may be non-JSON on error pages */ }
      console.log(`cloudPush attempt ${attempt}:`, r.status, resBody)
      if (r.ok) return true
      // Transient → wait and retry; permanent (401/403/400) → stop
      if ([502, 503, 500, 429].includes(r.status) && attempt < 4) {
        await new Promise(res => setTimeout(res, attempt * 700))
        continue
      }
      return false
    } catch (e) {
      console.warn(`cloudPush attempt ${attempt} threw:`, e.message)
      if (attempt < 4) { await new Promise(res => setTimeout(res, attempt * 700)); continue }
      return false
    }
  }
  return false
}

// ── Auto-sync: debounced push + silent pull ───────────
let _autoPushTimer = null
let _pushPending = false
let _autoPushRunning = false
let _autoPushQueued = false
function debouncedAutoPush() {
  clearTimeout(_autoPushTimer)
  _pushPending = true
  _autoPushTimer = setTimeout(async () => {
    if (_autoPushRunning) { _autoPushQueued = true; return }
    _autoPushRunning = true
    try {
      do {
        _autoPushQueued = false
        const success = await pushAtomicSnapshot()
        if (success) {
          if (!hasPendingSyncOperations() && !_autoPushQueued) { _localDirty = false; _pendingChangeCount = 0 }
          S.lastSyncPush = new Date().toISOString(); _lastSyncError = null; setSyncProgress(hasPendingSyncOperations() ? 'Finishing queued changes…' : 'Sync complete', 'complete')
        }
        else if (!_lastSyncError) _lastSyncError = { at:new Date().toISOString(), operation:'push', message:'Atomic sync failed; no partial cloud update was committed' }
      } while (_autoPushQueued)
    } finally {
      _autoPushRunning = false
      _pushPending = false
      setTimeout(function(){ if (!_syncInFlight && !_pullInFlight && !_pushPending) setSyncProgress(null) }, 1800)
    }
  }, 1500)
}

// Pull-only: fetch cloud state without pushing local first.
// Used on startup and focus when no local edits are pending.
let _pullInFlight = false
async function fetchAllSyncDomains() {
  const incoming = {}
  let fetchFail = 0
  // Domains are independent. Fetching them concurrently changes phone startup
  // from a long serial wait to the duration of the slowest domain.
  await Promise.all(SYNC_KEYS.map(async function(k) {
    let data
    if (CHUNKED_KEYS.includes(k)) data = await pullChunked(k)
    else data = await pullPiece(k)
    if (data === undefined) { fetchFail++; return }
    incoming[k] = data
  }))
  return { incoming, fetchFail }
}
async function pullOnly() {
  // Never replace application state or rerender while the user is editing a modal form.
  // A focus/periodic pull can otherwise erase unsaved fields before Save is clicked.
  if (S.modal) { S._syncRefreshDeferred = true; return }
  if (_pullInFlight || _pushPending || _localDirty) return
  if (!S.settings.cloudUrl || !S.settings.cloudToken || !S.settings.autoSyncEnabled) return
  _pullInFlight = true
  setSyncProgress('Checking cloud for changes…', 'checking')
  _lastSyncAttempt = { at: new Date().toISOString(), operation: 'pull' }
  try {
    const atomicCloud = await fetchAtomicSnapshot()
    if (atomicCloud && atomicCloud.snapshot) {
      const isUninitializedDevice = !!S._blockAutoPushUntilData || dataRecordCount() === 0
      const changed = await applyCloudSnapshotSafely(atomicCloud, 'pull', { trustCloud:isUninitializedDevice })
      if (_lastSyncError && _lastSyncError.operation === 'atomic-pull') { _pullInFlight=false; return }
      S.lastSyncPull = new Date().toISOString()
      _lastSyncError = null
      _pullInFlight=false
      setSyncProgress(changed ? 'Cloud changes applied' : 'Already current', 'complete')
      if (changed || S.tab === 'syncstatus') render()
      if (changed) showToast('☁️ Synced committed cloud snapshot', 2500)
      renderSyncIndicator()
      setTimeout(function(){ if (!_syncInFlight && !_pushPending) setSyncProgress(null) },1800)
      return
    }
    if (atomicCloud === undefined) { _pullInFlight=false; return }
    // No v2 snapshot exists yet. Read the legacy domains once so the first
    // updated device can migrate safely without losing existing cloud data.
    const fetched = await fetchAllSyncDomains()
    const incoming = fetched.incoming
    const fetchFail = fetched.fetchFail
    if (fetchFail > 0 || SYNC_KEYS.some(function(key) { return !Array.isArray(incoming[key]) })) {
      _lastSyncError = { at:new Date().toISOString(), operation:'pull', message:'Legacy cloud download was incomplete; nothing was applied' }
      _pullInFlight = false
      return
    }
    const legacyValidation = validateAtomicSnapshot({format:ATOMIC_SYNC_FORMAT,domains:incoming,tombstones:{}},currentDomainsObject(),SYNC_KEYS)
    if (!legacyValidation.ok) {
      _lastSyncError={at:new Date().toISOString(),operation:'atomic-pull',message:'Legacy cloud rollback blocked: '+legacyValidation.errors.join('; ')}
      if (typeof debugLogEvent==='function') debugLogEvent('error','SYNC_REGRESSION_BLOCKED',_lastSyncError.message)
      _pullInFlight=false
      return
    }

    const localCount = dataRecordCount()
    const cloudCount = SYNC_KEYS.reduce((sum, k) => sum + ((incoming[k]||[]).length), 0)
    if (localCount > 0 && cloudCount < localCount * 0.5) {
      console.warn(`pullOnly: cloud (${cloudCount}) < 50% of local (${localCount}) — skipped`)
      _pullInFlight = false; return
    }

    // Re-check: if the user saved during the pull fetch, abort — don't overwrite their edit
    if (_localDirty || _pushPending || S.modal) {
      if (S.modal) S._syncRefreshDeferred = true
      console.log('pullOnly: local edit or active form detected during fetch — aborting apply')
      _pullInFlight = false; return
    }

    let changed = false
    for (const k of SYNC_KEYS) {
      if (incoming[k] === undefined || incoming[k] === null) continue
      if (JSON.stringify(S[k]) !== JSON.stringify(incoming[k])) {
        S[k] = incoming[k]; changed = true
      }
      if (!S._syncBaseline) S._syncBaseline = {}
      S._syncBaseline[k] = cloneSyncValue(incoming[k])
      if (S._syncPendingOps) delete S._syncPendingOps[k]
    }
    if (changed) {
      migrateLegacy()
      repairDataIntegrity(S)
      await writeCurrentStateLocalOnly()
      render()
      showToast('☁️ Synced from cloud', 2500)
    } else await writeCurrentStateLocalOnly()
    S.lastSyncPull = new Date().toISOString()
    _lastSyncError = null
    renderSyncIndicator()
  } catch (e) {
    _lastSyncError = { at: new Date().toISOString(), operation: 'pull', message: e.message || 'Pull failed' }
    console.warn('pullOnly error:', e.message)
  }
  _pullInFlight = false
}

// Push-then-pull: used by manual sync buttons only.
// Always pushes local state to cloud FIRST so edits are never overwritten,
// then pulls to pick up changes from other devices.
let _syncInFlight = false
async function pushThenPull() {
  if (S.modal) { S._syncRefreshDeferred = true; showToast('Finish or cancel the open form before syncing'); return }
  if (_syncInFlight) return
  if (!S.settings.cloudUrl || !S.settings.cloudToken || !S.settings.autoSyncEnabled) return
  _syncInFlight = true
  setSyncProgress('Starting manual sync…', 'starting')
  _lastSyncAttempt = { at: new Date().toISOString(), operation: 'sync' }
  const hadLocalChanges = _localDirty
  try {
    await writeLocalBackup('before-manual-sync')
    const existingAtomic = await fetchAtomicSnapshot()
    if (existingAtomic && existingAtomic.snapshot && !S._syncSnapshotRevision && !_localDirty) {
      const adopted = await applyCloudSnapshotSafely(existingAtomic, 'manual-sync-adopt')
      if (_lastSyncError && _lastSyncError.operation === 'atomic-pull') { _syncInFlight=false; return }
      if (adopted) render()
    }
    if (_localDirty) {
      const pushed = await pushAtomicSnapshot()
      if (!pushed) { _syncInFlight=false; return }
      if (!hasPendingSyncOperations()) { _localDirty=false; _pendingChangeCount=0 }
      S.lastSyncPush=new Date().toISOString()
    }
    const committed = await fetchAtomicSnapshot()
    if (committed && committed.snapshot) {
      const changed = await applyCloudSnapshotSafely(committed, 'manual-sync')
      if (_lastSyncError && _lastSyncError.operation === 'atomic-pull') { _syncInFlight=false; return }
      S.lastSyncPull=new Date().toISOString(); _lastSyncError=null
      setSyncProgress('Sync complete', 'complete')
      render(); renderSyncIndicator()
      if (!hadLocalChanges && !changed) showToast('☁️ No changes; committed snapshot verified',3000)
      _syncInFlight=false
      return
    }
    if (committed === undefined) { _syncInFlight=false; return }
    // Legacy fallback remains read-only until a local save seeds v2.
    // ── PUSH phase: only push if local has unsaved changes ──
    // If this device hasn't been edited since last sync, pushing would overwrite
    // the cloud with stale local data from a prior session.
    if (_localDirty) {
      var pushFailures = 0
      for (const k of SYNC_KEYS) {
        var pushed = CHUNKED_KEYS.includes(k) ? await pushChunked(k) : await pushPiece(k, S[k] || [])
        if (!pushed) pushFailures++
      }
      if (!pushFailures) { _localDirty = false; _pendingChangeCount = 0; S.lastSyncPush = new Date().toISOString() }
      else { _lastSyncError = { at:new Date().toISOString(), operation:'push', message:pushFailures + ' sync domain(s) failed' }; _syncInFlight = false; render(); return }
    }

    // ── PULL phase: fetch cloud state (may include changes from other devices) ──
    const fetched = await fetchAllSyncDomains()
    const incoming = fetched.incoming
    const fetchFail = fetched.fetchFail
    if (fetchFail > 0 || SYNC_KEYS.some(function(key) { return !Array.isArray(incoming[key]) })) {
      _lastSyncError = { at:new Date().toISOString(), operation:'sync', message:'Legacy cloud download was incomplete; nothing was applied' }
      _syncInFlight = false
      return
    }
    const legacyValidation = validateAtomicSnapshot({format:ATOMIC_SYNC_FORMAT,domains:incoming,tombstones:{}},currentDomainsObject(),SYNC_KEYS)
    if (!legacyValidation.ok) {
      _lastSyncError={at:new Date().toISOString(),operation:'atomic-pull',message:'Legacy cloud rollback blocked: '+legacyValidation.errors.join('; ')}
      if (typeof debugLogEvent==='function') debugLogEvent('error','SYNC_REGRESSION_BLOCKED',_lastSyncError.message)
      _syncInFlight=false
      return
    }

    const localCount = dataRecordCount()
    const cloudCount = SYNC_KEYS.reduce((sum, k) => sum + ((incoming[k]||[]).length), 0)
    if (localCount > 0 && cloudCount < localCount * 0.5) {
      console.warn(`pushThenPull: cloud (${cloudCount}) < 50% of local (${localCount}) after push — pull skipped`)
      _syncInFlight = false; renderSyncIndicator(); return
    }

    // Re-check: if user saved during the pull phase, abort apply
    if (_localDirty || _pushPending || S.modal) {
      if (S.modal) S._syncRefreshDeferred = true
      console.log('pushThenPull: local edit or active form during pull phase — aborting apply')
      _syncInFlight = false; return
    }

    let changed = false
    for (const k of SYNC_KEYS) {
      if (incoming[k] === undefined || incoming[k] === null) continue
      if (JSON.stringify(S[k]) !== JSON.stringify(incoming[k])) {
        S[k] = incoming[k]; changed = true
      }
      if (!S._syncBaseline) S._syncBaseline = {}
      S._syncBaseline[k] = cloneSyncValue(incoming[k])
      if (S._syncPendingOps) delete S._syncPendingOps[k]
    }
    if (changed) {
      migrateLegacy()
      repairDataIntegrity(S)
      S._allowEmptyPush = false
      await writeCurrentStateLocalOnly()
      render()
      showToast('☁️ Synced from cloud', 2500)
    } else await writeCurrentStateLocalOnly()
    S.lastSyncPull = new Date().toISOString()
    _lastSyncError = null
    renderSyncIndicator()
    if (!hadLocalChanges && !changed) showToast('☁️ No local changes; pull completed', 3000)
  } catch (e) {
    _lastSyncError = { at: new Date().toISOString(), operation: 'sync', message: e.message || 'Sync failed' }
    console.warn('pushThenPull error:', e.message)
  }
  _syncInFlight = false
  setTimeout(function(){ if (!_pullInFlight && !_pushPending) setSyncProgress(null) },1800)
}

function getSyncRuntimeStatus() {
  const configured = !!(S.settings && S.settings.cloudUrl && S.settings.cloudToken)
  const online = typeof navigator === 'undefined' ? true : navigator.onLine !== false
  let state = 'synced'
  if (!configured) state = 'not-configured'
  else if (!online) state = 'offline'
  else if (_lastSyncError) state = 'error'
  else if (_syncInFlight || _pullInFlight || _pushPending) state = 'syncing'
  else if (_localDirty) state = 'pending'
  else if (!S.lastSyncPull && !S.lastSyncPush) state = 'never-synced'
  return {
    state, configured, online,
    dirty: _localDirty,
    pendingChangeCount: _pendingChangeCount,
    deviceId: syncDeviceId(),
    inFlight: _syncInFlight || _pullInFlight || _pushPending,
    lastPush: S.lastSyncPush || null,
    lastPull: S.lastSyncPull || null,
    lastAttempt: _lastSyncAttempt,
    error: _lastSyncError,
    domains: Object.assign({}, _syncDomainStatus),
    conflicts: Object.keys(_syncConflicts).map(function(k) { return _syncConflicts[k] }),
    conflictDetails: Object.assign({}, _syncConflictDetails),
    totalRecords: dataRecordCount(),
    domainCount: SYNC_KEYS.length
    ,atomicRevision:S._syncSnapshotRevision || null
    ,lastPreview:S._lastSyncPreview || null
    ,progress:_syncProgress
  }
}

// Render the "last synced" indicator in the header
function renderSyncIndicator() {
  const el = document.getElementById('sync-indicator')
  const sidebarEl = document.getElementById('sidebar-sync-status')
  if (!el && !sidebarEl) return
  if (!S.settings.cloudUrl || !S.settings.cloudToken) { if(el)el.innerHTML=''; if(sidebarEl)sidebarEl.textContent='Not configured'; return }
  if (_syncProgress) {
    if (el) el.innerHTML = '<span style="font-size:10px;color:var(--accentT)">↻ ' + esc(_syncProgress.message) + '</span>'
    if (sidebarEl) sidebarEl.textContent = _syncProgress.message
    return
  }
  const last = S.lastSyncPull || S.lastSyncPush
  if (!last) { if(el)el.innerHTML = '<span style="font-size:10px;color:var(--text4)">☁️ not synced</span>'; if(sidebarEl)sidebarEl.textContent='Not synced'; return }
  const ago = timeAgo(new Date(last))
  if (el) el.innerHTML = `<span style="font-size:10px;color:var(--text4)" title="Last sync: ${esc(last)}">☁️ ${ago}</span>`
  if (sidebarEl) sidebarEl.textContent = 'Synced ' + ago
}
function timeAgo(d) {
  if (!(d instanceof Date)) d = new Date(d)
  if (Number.isNaN(d.getTime())) return 'Unknown'
  const s = Math.floor((Date.now() - d.getTime()) / 1000)
  if (s < 10) return 'just now'
  if (s < 60) return s + 's ago'
  const m = Math.floor(s / 60)
  if (m < 60) return m + 'm ago'
  const h = Math.floor(m / 60)
  if (h < 24) return h + 'h ago'
  return Math.floor(h / 24) + 'd ago'
}

// Periodic background pull (optional, 0 = disabled)
let _periodicPullTimer = null
function startPeriodicPull() {
  clearInterval(_periodicPullTimer)
  const min = S.settings.autoSyncIntervalMin
  if (!min || min <= 0) return
  _periodicPullTimer = setInterval(() => { if (!_localDirty && !_pushPending) pullOnly() }, min * 60 * 1000)
}

// Normalize a cloud sync URL: trim, fix double slashes, ensure /api/data path
function normalizeCloudUrl(raw) {
  let u = (raw || '').trim()
  if (!u) return ''
  // Add scheme if missing
  if (!/^https?:\/\//i.test(u)) u = 'https://' + u
  // Collapse accidental double slashes in the path (but not after the scheme)
  u = u.replace(/([^:]\/)\/+/g, '$1')
  // Strip a trailing slash
  u = u.replace(/\/+$/, '')
  // If it points at the bare origin (no /api path), append /api/data
  try {
    const parsed = new URL(u)
    if (!parsed.pathname || parsed.pathname === '/' || parsed.pathname === '') {
      u = parsed.origin + '/api/data'
    }
  } catch (e) { /* leave as-is if unparseable */ }
  return u
}

async function cloudFetch() {
  if (!S.settings.cloudUrl || !S.settings.cloudToken) return null
  const url = normalizeCloudUrl(S.settings.cloudUrl)
  for (let attempt = 1; attempt <= 4; attempt++) {
    try {
      const r = await fetch(url, { headers: { 'X-Token': S.settings.cloudToken } })
      const text = await r.text()
      // Transient server error → wait and retry
      if ([502, 503, 500, 429].includes(r.status) && attempt < 4) {
        console.warn(`cloudFetch attempt ${attempt}: ${r.status}, retrying…`)
        await new Promise(res => setTimeout(res, attempt * 700))
        continue
      }
      let body
      try { body = JSON.parse(text) }
      catch (parseErr) {
        console.warn('cloudFetch: non-JSON response from', url, '→', text.slice(0, 120))
        return { ok: false, status: r.status, error: `Server busy (got ${r.status}). Try again in a moment.` }
      }
      console.log('cloudFetch response:', r.status, body)
      if (!r.ok) return { ok: false, status: r.status, error: body.error || `Server returned ${r.status}` }
      return body
    } catch (e) {
      console.warn(`cloudFetch attempt ${attempt} threw:`, e.message)
      if (attempt < 4) { await new Promise(res => setTimeout(res, attempt * 700)); continue }
      return { ok: false, networkError: true, error: `Could not reach ${url} (${e.message})` }
    }
  }
  return { ok: false, networkError: true, error: 'Server kept returning errors. Try again shortly.' }
}

// Build the per-key endpoint URL: normalized base + ?key=NAME for piece-by-piece sync
function cloudUrlForKey(key) {
  const base = normalizeCloudUrl(S.settings.cloudUrl)
  if (!base) return ''
  return base + (base.includes('?') ? '&' : '?') + 'key=' + encodeURIComponent(key)
}

const SYNC_KEYS = ['peptides','documents','storageLocations','injections','healthData','notes','labResults','supplements','users','peptideLibrary','changeLog','customCategories','refillLog','injectionSupplies','peptidePens','vendors','cycleTimelines','blends','protocols','scenarios','inventoryReservations','regimenVersions','coas','labMarkerDictionary','savedViews','trash','snapshots','reportPresets','masterPlanRules','protocolDependencies','vaultPositions','vaultAssignments','vaultMovements','vaultReconciliations','protocolRequirements','mobilePickingSheets','supplementLabelReviews','preparationBatches']

// Keys that get split by year for sync (too large for a single D1 row over time)
const CHUNKED_KEYS = ['healthData', 'injections']

// Extract unique years from a dated array (records must have a .date field)
function yearsInArray(arr) {
  const yrs = new Set()
  for (const r of (arr || [])) {
    const d = r.date || r.dateTime || ''
    const y = d.substring(0, 4)
    if (/^\d{4}$/.test(y)) yrs.add(y)
  }
  return [...yrs].sort()
}

// Push a chunked key: split by year, push each year chunk + a manifest of years
async function pushChunked(key) {
  const arr = S[key] || []
  const byYear = {}
  for (const r of arr) {
    const y = (r.date || r.dateTime || '').substring(0, 4)
    if (!/^\d{4}$/.test(y)) continue
    ;(byYear[y] = byYear[y] || []).push(r)
  }
  const years = Object.keys(byYear).sort()
  let ok = 0
  for (const y of years) {
    if (await pushPiece(`${key}_${y}`, byYear[y])) ok++
  }
  // Manifest lists which year keys exist so pull knows what to fetch
  if (await pushPiece(`${key}_manifest`, years)) ok++
  // Every year plus the manifest must succeed. Reporting success after only
  // one piece landed can clear the dirty flag while older years remain stale.
  return ok === years.length + 1
}

// Pull a chunked key: read manifest, pull each year chunk, reassemble
async function pullChunked(key) {
  const manifest = await pullPiece(`${key}_manifest`)
  if (!manifest || !Array.isArray(manifest) || manifest.length === 0) {
    // No manifest — fall back to pulling the old single key (migration path)
    return pullPiece(key)
  }
  let combined = []
  for (const y of manifest) {
    const chunk = await pullPiece(`${key}_${y}`)
    if (!Array.isArray(chunk)) {
      _syncDomainStatus[key] = { operation:'pull', state:'error', at:new Date().toISOString(), message:'One or more yearly chunks could not be downloaded; local data preserved' }
      return undefined
    }
    combined = combined.concat(chunk)
  }
  _syncDomainStatus[key] = { operation:'pull', state:'success', at:new Date().toISOString(), message:manifest.length + ' yearly chunks' }
  return combined
}

// Push one key's array as its own small request, with 503/network retry
function syncPayloadEquivalent(left, right) {
  var volatile = { updatedAt:true, createdAt:true, addedAt:true, assignedAt:true, extractedAt:true, at:true }
  function stable(value) {
    if (Array.isArray(value)) return value.map(stable)
    if (value && typeof value === 'object') {
      var out = {}
      Object.keys(value).sort().forEach(function(key){ if (!volatile[key]) out[key] = stable(value[key]) })
      return out
    }
    return value
  }
  return JSON.stringify(stable(left)) === JSON.stringify(stable(right))
}

async function pushPiece(key, value) {
  const url = cloudUrlForKey(key)
  if (!url) return false
  let pendingValue = value
  let automaticallyMerged = false
  // P2-6: Include updatedAt for conditional write — server can reject stale writes
  var headers = { 'Content-Type':'application/json', 'X-Token':S.settings.cloudToken, 'X-Device-ID':syncDeviceId() }
  var lastPull = S._syncRevisions && S._syncRevisions[key]
  if (lastPull) headers['If-Match'] = lastPull
  for (let attempt = 1; attempt <= 5; attempt++) {
    try {
      const r = await fetch(url, { method:'POST', headers: headers, body:JSON.stringify({ data:pendingValue }) })
      if (r.ok) {
        // Store server revision for next conditional write
        var rev = r.headers.get('ETag') || r.headers.get('X-Updated-At')
        if (rev) { if (!S._syncRevisions) S._syncRevisions = {}; S._syncRevisions[key] = rev }
        _syncDomainStatus[key] = { operation:'push', state:'success', at:new Date().toISOString(), revision:rev || null, sourceDevice:syncDeviceId() }
        delete _syncConflicts[key]
        delete _syncConflictDetails[key]
        markPieceSynced(key, pendingValue)
        if (automaticallyMerged) {
          applyAutomaticallyMergedPiece(key, pendingValue)
          await writeCurrentStateLocalOnly()
          if (typeof debugLogEvent === 'function') debugLogEvent('info', 'SYNC_AUTO_MERGED', key + ' reconciled automatically')
        }
        return true
      }
      if (r.status === 412) {
        // Conditional write rejected — data changed on server
        var conflictBody = {}; try { conflictBody = await r.json() } catch (e) {}
        // A second device can push the exact same records under a newer revision.
        // Refresh that revision automatically because no data choice is required.
        var identicalSnapshot = await fetchSyncConflictSnapshot(key)
        if (identicalSnapshot && syncPayloadEquivalent(identicalSnapshot.data, pendingValue)) {
          var identicalRevision = identicalSnapshot.revision || conflictBody.currentRevision || null
          if (identicalRevision) { if (!S._syncRevisions) S._syncRevisions = {}; S._syncRevisions[key] = identicalRevision }
          delete _syncConflicts[key]
          delete _syncConflictDetails[key]
          applyAutomaticallyMergedPiece(key, identicalSnapshot.data)
          markPieceSynced(key, identicalSnapshot.data)
          await writeCurrentStateLocalOnly()
          _syncDomainStatus[key] = { operation:'reconcile', state:'success', at:new Date().toISOString(), revision:identicalRevision, sourceDevice:identicalSnapshot.sourceDevice, message:'Equivalent cloud copy; revision refreshed' }
          if (typeof debugLogEvent === 'function') debugLogEvent('info', 'SYNC_RECONCILED', key + ' matched cloud; revision refreshed')
          return true
        }
        // Another device changed this domain first. Preserve records from both
        // sides, choose the newest timestamp for matching IDs, and retry against
        // the new revision without interrupting the user.
        if (identicalSnapshot && attempt < 5) {
          pendingValue = replayPendingOperations(key, identicalSnapshot.data)
          var mergeRevision = identicalSnapshot.revision || conflictBody.currentRevision || null
          if (mergeRevision) {
            if (!S._syncRevisions) S._syncRevisions = {}
            S._syncRevisions[key] = mergeRevision
            headers['If-Match'] = mergeRevision
          }
          automaticallyMerged = true
          _syncDomainStatus[key] = { operation:'reconcile', state:'syncing', at:new Date().toISOString(), revision:mergeRevision, sourceDevice:identicalSnapshot.sourceDevice, message:'Reconciling changes automatically' }
          continue
        }
        _syncDomainStatus[key] = { operation:'reconcile', state:'error', at:new Date().toISOString(), revision:conflictBody.currentRevision || null, message:'Automatic retry will continue on the next sync' }
        _lastSyncError = { at:new Date().toISOString(), operation:'push', message:'Automatic sync retry pending for ' + key }
        if (typeof debugLogEvent === 'function') debugLogEvent('warn', 'SYNC_RETRY_PENDING', key + ' changed repeatedly during automatic reconciliation')
        return false
      }
      if ([502,503,500,429].includes(r.status) && attempt < 5) {
        var retryAfter = Number(r.headers.get('Retry-After') || 0)
        var delay = retryAfter > 0 ? Math.min(retryAfter * 1000, 65000) : attempt * 1200
        await new Promise(s=>setTimeout(s, delay)); continue
      }
      var errorText = ''
      try { var errorBody = await r.json(); errorText = errorBody.error || '' } catch (e) {}
      var errorMessage = 'HTTP ' + r.status + (errorText ? ': ' + errorText : '')
      _syncDomainStatus[key] = { operation:'push', state:'error', at:new Date().toISOString(), message:errorMessage }
      if (typeof debugLogEvent === 'function') debugLogEvent('error', 'SYNC_PUSH_ERROR', key + ' failed: ' + errorMessage)
      return false
    } catch (e) {
      if (attempt < 5) { await new Promise(s=>setTimeout(s, attempt*1200)); continue }
      var networkMessage = e && e.message ? e.message : 'Network request failed'
      _syncDomainStatus[key] = { operation:'push', state:'error', at:new Date().toISOString(), message:networkMessage }
      if (typeof debugLogEvent === 'function') debugLogEvent('error', 'SYNC_PUSH_ERROR', key + ' failed: ' + networkMessage)
      return false
    }
  }
  return false
}

function applyAutomaticallyMergedPiece(key, records) {
  if (SYNC_KEYS.includes(key)) { S[key] = records; return }
  var match = key.match(/^(healthData|injections)_(\d{4})$/)
  if (!match) return
  var domain = match[1], year = match[2]
  S[domain] = (S[domain] || []).filter(function(record) {
    return String(record.date || record.dateTime || '').slice(0, 4) !== year
  }).concat(records || [])
}

// Pull one key's array; returns the data, null (empty on server), or undefined (failed)
async function pullPiece(key) {
  const url = cloudUrlForKey(key)
  if (!url) return undefined
  for (let attempt = 1; attempt <= 5; attempt++) {
    try {
      const r = await fetch(url, { headers:{ 'X-Token':S.settings.cloudToken, 'X-Device-ID':syncDeviceId() } })
      if ([502,503,500,429].includes(r.status) && attempt < 5) { await new Promise(s=>setTimeout(s, attempt*1200)); continue }
      const text = await r.text()
      let body
      try { body = JSON.parse(text) } catch (e) {
        var parseMessage = 'HTTP ' + r.status + ': invalid server response'
        _syncDomainStatus[key] = { operation:'pull', state:'error', at:new Date().toISOString(), message:parseMessage }
        if (typeof debugLogEvent === 'function') debugLogEvent('error', 'SYNC_PULL_ERROR', key + ' failed: ' + parseMessage)
        return undefined
      }
      if (!r.ok) {
        var pullMessage = 'HTTP ' + r.status + (body.error ? ': ' + body.error : '')
        _syncDomainStatus[key] = { operation:'pull', state:'error', at:new Date().toISOString(), message:pullMessage }
        if (typeof debugLogEvent === 'function') debugLogEvent('error', 'SYNC_PULL_ERROR', key + ' failed: ' + pullMessage)
        return undefined
      }
      var rev = r.headers.get('ETag') || r.headers.get('X-Updated-At') || body.updatedAt || null
      var sourceDevice = r.headers.get('X-Source-Device') || body.sourceDevice || null
      if (rev) { if (!S._syncRevisions) S._syncRevisions = {}; S._syncRevisions[key] = rev }
      _syncDomainStatus[key] = { operation:'pull', state:'success', at:new Date().toISOString(), revision:rev, sourceDevice:sourceDevice }
      return body.data
    } catch (e) {
      if (attempt < 5) { await new Promise(s=>setTimeout(s, attempt*1200)); continue }
      var pullNetworkMessage = e && e.message ? e.message : 'Network request failed'
      _syncDomainStatus[key] = { operation:'pull', state:'error', at:new Date().toISOString(), message:pullNetworkMessage }
      if (typeof debugLogEvent === 'function') debugLogEvent('error', 'SYNC_PULL_ERROR', key + ' failed: ' + pullNetworkMessage)
      return undefined
    }
  }
  return undefined
}

async function fetchSyncConflictSnapshot(key) {
  const url = cloudUrlForKey(key)
  if (!url) return null
  try {
    const r = await fetch(url, { headers:{ 'X-Token':S.settings.cloudToken, 'X-Device-ID':syncDeviceId() } })
    const body = await r.json()
    if (!r.ok) throw new Error(body.error || ('HTTP ' + r.status))
    return { data:Array.isArray(body.data) ? body.data : [], revision:r.headers.get('ETag') || r.headers.get('X-Updated-At') || body.updatedAt || null, sourceDevice:r.headers.get('X-Source-Device') || body.sourceDevice || null }
  } catch (e) {
    _lastSyncError = { at:new Date().toISOString(), operation:'conflict-review', message:e.message || 'Could not load cloud copy' }
    showToast('❌ Could not load cloud copy')
    return null
  }
}

function summarizeSyncDifference(local, cloud) {
  var localById = {}, cloudById = {}, added = 0, removed = 0, modified = 0
  ;(local || []).forEach(function(item) { if (item && item.id) localById[item.id] = item })
  ;(cloud || []).forEach(function(item) { if (item && item.id) cloudById[item.id] = item })
  Object.keys(cloudById).forEach(function(id) { if (!localById[id]) added++; else if (JSON.stringify(localById[id]) !== JSON.stringify(cloudById[id])) modified++ })
  Object.keys(localById).forEach(function(id) { if (!cloudById[id]) removed++ })
  return { localCount:(local || []).length, cloudCount:(cloud || []).length, added, removed, modified }
}

function mergeSyncRecords(local, cloud) {
  var byId = {}, idless = {}, result = []
  function add(item) {
    if (!item || !item.id) { var sig = JSON.stringify(item); if (!idless[sig]) { idless[sig] = true; result.push(item) }; return }
    var existing = byId[item.id]
    if (!existing) { byId[item.id] = item; return }
    var existingTime = existing.updatedAt || existing.createdAt || ''
    var itemTime = item.updatedAt || item.createdAt || ''
    if (itemTime > existingTime) byId[item.id] = item
  }
  ;(cloud || []).forEach(add); (local || []).forEach(add)
  return Object.keys(byId).map(function(id) { return byId[id] }).concat(result)
}

async function reviewSyncConflict(key) {
  if (!_syncConflicts[key]) return
  var snap = await fetchSyncConflictSnapshot(key)
  if (!snap) return
  _syncConflictDetails[key] = { key:key, local:S[key] || [], cloud:snap.data, revision:snap.revision, sourceDevice:snap.sourceDevice, summary:summarizeSyncDifference(S[key] || [], snap.data) }
  render()
}

async function resolveSyncDomainConflict(key, choice) {
  var detail = _syncConflictDetails[key]
  if (!detail) { await reviewSyncConflict(key); detail = _syncConflictDetails[key] }
  if (!detail) return
  await writeLocalBackup('before-sync-conflict-' + choice)
  if (typeof logAuditEvent === 'function') logAuditEvent('sync', key, 'resolve-' + choice, 'Resolved Local vs Cloud conflict for ' + key + ' using ' + choice + ' on device ' + syncDeviceId())
  var ok = true
  if (choice === 'cloud') {
    S[key] = detail.cloud
    if (detail.revision) S._syncRevisions[key] = detail.revision
    await writeCurrentStateLocalOnly()
  } else {
    if (choice === 'merge') S[key] = mergeSyncRecords(detail.local, detail.cloud)
    if (detail.revision) S._syncRevisions[key] = detail.revision
    ok = await pushPiece(key, S[key] || [])
  }
  if (!ok) { showToast('⚠️ Cloud changed again; review the refreshed conflict'); await reviewSyncConflict(key); return }
  delete _syncConflicts[key]; delete _syncConflictDetails[key]
  _lastSyncError = Object.keys(_syncConflicts).length ? _lastSyncError : null
  _syncDomainStatus[key] = { operation:'resolve', state:'success', at:new Date().toISOString(), revision:S._syncRevisions[key] || null }
  if (choice !== 'cloud') await save()
  render(); showToast(choice === 'cloud' ? '✅ Cloud copy applied' : choice === 'merge' ? '✅ Copies merged and synced' : '✅ Local copy kept and synced')
}

// A new or stale browser should be able to become a faithful cloud replica in
// one deliberate action. The cloud is read-only during this operation and the
// previous device state is backed up before anything local is replaced.
async function restoreThisDeviceFromCloud() {
  if (_syncInFlight || _pullInFlight || _pushPending) { showToast('A sync is already running'); return }
  if (S.modal) { showToast('Finish or cancel the open form first'); return }
  if (!navigator.onLine) { showToast('❌ This device is offline'); return }
  if (!S.settings.cloudUrl || !S.settings.cloudToken) { showToast('❌ Set a sync URL and token in Settings first'); return }
  const proceed = await asyncConfirm(
    'Restore this device from the cloud?\n\n' +
    'This replaces the data cached on this device with the complete cloud copy and clears its stale conflicts. ' +
    'Nothing will be written to or deleted from the cloud. A local recovery copy will be created first.'
  )
  if (!proceed) return

  _syncInFlight = true
  const previousRevisions = Object.assign({}, S._syncRevisions || {})
  try {
    showToast('Creating a local recovery copy…')
    await writeLocalBackup('before-device-cloud-restore')

    const atomicCloud = await fetchAtomicSnapshot()
    if (atomicCloud && atomicCloud.snapshot) {
      const validation=validateAtomicSnapshot(atomicCloud.snapshot,{},SYNC_KEYS)
      if (!validation.ok) throw new Error(validation.errors.join('; '))
      applyAtomicSnapshot(atomicCloud.snapshot,atomicCloud.revision)
      migrateLegacy(); repairDataIntegrity(S)
      S._allowEmptyPush=false; S._blockAutoPushUntilData=false
      S.lastSyncPull=new Date().toISOString(); _localDirty=false; _pendingChangeCount=0
      _syncConflicts={}; _syncConflictDetails={}; _lastSyncError=null
      _lastSyncAttempt={at:S.lastSyncPull,operation:'device-cloud-restore',ok:true}
      await writeCurrentStateLocalOnly(); render()
      showToast(`✅ This device now matches committed cloud snapshot (${dataRecordCount()} records)`)
      return
    }
    if (atomicCloud === undefined) throw new Error('Could not download committed cloud snapshot')

    const incoming = {}
    for (let i = 0; i < SYNC_KEYS.length; i++) {
      const key = SYNC_KEYS[i]
      showToast(`Downloading ${key} (${i + 1}/${SYNC_KEYS.length})…`)
      const data = CHUNKED_KEYS.includes(key) ? await pullChunked(key) : await pullPiece(key)
      if (data === undefined) throw new Error('Could not download ' + key + '; this device was not changed')
      incoming[key] = Array.isArray(data) ? data : []
      await new Promise(resolve => setTimeout(resolve, 120))
    }

    const cloudCount = dataRecordCount(incoming)
    if (cloudCount === 0 && dataRecordCount() > 0) throw new Error('Cloud copy is empty; this device was not changed')

    SYNC_KEYS.forEach(function(key) { S[key] = incoming[key] })
    migrateLegacy()
    S._allowEmptyPush = false
    S._blockAutoPushUntilData = false
    S.lastSyncPull = new Date().toISOString()
    _localDirty = false
    _pendingChangeCount = 0
    _syncConflicts = {}
    _syncConflictDetails = {}
    _lastSyncError = null
    _lastSyncAttempt = { at:S.lastSyncPull, operation:'device-cloud-restore', ok:true }
    await writeCurrentStateLocalOnly()
    render()
    showToast(`✅ This device now matches the cloud (${cloudCount} records)`)
  } catch (e) {
    S._syncRevisions = previousRevisions
    _lastSyncError = { at:new Date().toISOString(), operation:'device-cloud-restore', message:e.message || 'Restore failed' }
    render()
    showToast('❌ ' + (_lastSyncError.message || 'Restore failed'))
  } finally {
    _syncInFlight = false
  }
}

async function syncFromCloud() {
  if (S.modal) { S._syncRefreshDeferred = true; showToast('Finish or cancel the open form before pulling'); return }
  if (!S.settings.cloudUrl || !S.settings.cloudToken) { showToast('❌ Set a sync URL and token in Settings first'); return }

  // Snapshot what's coming so we can warn before a destructive pull
  showToast('Checking cloud…')
  const atomicCloud=await fetchAtomicSnapshot()
  if (atomicCloud && atomicCloud.snapshot) {
    const changed=await applyCloudSnapshotSafely(atomicCloud,'manual-pull')
    if (_lastSyncError && _lastSyncError.operation==='atomic-pull') { showToast('❌ Unsafe cloud rollback blocked'); return }
    S.lastSyncPull=new Date().toISOString(); render(); renderSyncIndicator()
    showToast(changed?'✅ Committed cloud snapshot applied':'✅ Already matches committed cloud snapshot')
    return
  }
  if (atomicCloud===undefined) { showToast('❌ Could not reach cloud — nothing changed'); return }
  const incoming = {}
  let fetchFail = 0
  for (const k of SYNC_KEYS) {
    let data
    if (CHUNKED_KEYS.includes(k)) {
      data = await pullChunked(k)
    } else {
      data = await pullPiece(k)
    }
    if (data === undefined) { fetchFail++; continue }
    incoming[k] = data   // may be null (empty on server) or an array
    await new Promise(resolve => setTimeout(resolve, 300))
  }
  // Legacy migration is all-or-nothing too. A partial set can never become an
  // authoritative snapshot or replace any current device data.
  if (fetchFail > 0 || SYNC_KEYS.some(function(key) { return !Array.isArray(incoming[key]) })) {
    showToast('❌ Cloud download was incomplete — nothing changed'); return
  }
  const legacyValidation=validateAtomicSnapshot({format:ATOMIC_SYNC_FORMAT,domains:incoming,tombstones:{}},currentDomainsObject(),SYNC_KEYS)
  if (!legacyValidation.ok) {
    _lastSyncError={at:new Date().toISOString(),operation:'atomic-pull',message:'Legacy cloud rollback blocked: '+legacyValidation.errors.join('; ')}
    if (typeof debugLogEvent==='function') debugLogEvent('error','SYNC_REGRESSION_BLOCKED',_lastSyncError.message)
    showToast('❌ Older cloud data was blocked — this device was not changed')
    return
  }

  // Compare totals. If the cloud has dramatically less data than local, this pull would
  // destroy data — require explicit confirmation first, and always back up locally.
  const localCount = dataRecordCount()
  const cloudCount = SYNC_KEYS.reduce((sum, k) => sum + ((incoming[k]||[]).length), 0)
  if (localCount > 0 && cloudCount < localCount * 0.5) {
    const proceed = await asyncConfirm(
      `⚠️ Cloud has noticeably less data than this device.\n\n` +
      `This device: ${localCount} records\nCloud: ${cloudCount} records\n\n` +
      `Pulling will REPLACE this device's data with the cloud's smaller set. ` +
      `A local backup will be saved first. Continue?`
    )
    if (!proceed) { showToast('Pull cancelled — your data is unchanged'); return }
  }

  // Always create a timestamped local backup before overwriting
  await writeLocalBackup('before-pull')

  let ok = 0
  for (const k of SYNC_KEYS) {
    if (incoming[k] === undefined) continue   // fetch failed → keep local
    if (incoming[k] === null) continue        // empty on server → keep local (don't wipe)
    S[k] = incoming[k]; ok++
  }
  migrateLegacy()
  S._allowEmptyPush = false
  await save()
  S.lastSyncPull = new Date().toISOString()
  render()
  showToast(`✅ Synced ${ok} types from cloud`)
}

async function syncToCloud() {
  if (S.modal) { showToast('Finish or cancel the open form before pushing'); return }
  if (!S.settings.cloudUrl || !S.settings.cloudToken) { showToast('❌ Set a sync URL and token in Settings first'); return }
  // SAFETY: don't let a manual push overwrite the cloud with an empty/collapsed dataset
  const count = dataRecordCount()
  if (count === 0) {
    const proceed = await asyncConfirm('⚠️ This device has NO data. Pushing will overwrite the cloud with an empty dataset, erasing the cloud backup.\n\nOnly do this if you intend to wipe the cloud. Continue?')
    if (!proceed) { showToast('Push cancelled — cloud is unchanged'); return }
    S._allowEmptyPush = true
  }
  captureSyncOperations()
  const success=await pushAtomicSnapshot()
  if (success) { _localDirty=false; _pendingChangeCount=0; S.lastSyncPush=new Date().toISOString(); _lastSyncError=null; renderSyncIndicator() }
  showToast(success?'✅ All data committed to cloud together':'⚠️ Cloud commit failed — no partial update was accepted')
}

// Recover from the legacy whole-blob backup (the pre-piece-sync key, untouched by piece-sync).
// Restores arrays that are larger in the backup than locally — never shrinks anything.
async function recoverFromWholeBlob() {
  if (!S.settings.cloudUrl || !S.settings.cloudToken) { showToast('❌ Set a sync URL and token first'); return }
  showToast('Fetching full backup…')
  const cloud = await cloudFetch()   // cloudFetch hits the base URL = the whole-blob key
  if (!cloud || cloud.networkError || !cloud.ok) { showToast('❌ ' + (cloud?.error || 'Could not fetch backup')); return }
  const blob = cloud.data
  if (!blob) { showToast('No full backup on server'); return }
  const msg = []
  const blobHealth = (blob.healthData||[]).length, localHealth = (S.healthData||[]).length
  if (blobHealth > localHealth) { S.healthData = blob.healthData; msg.push(`healthData ${localHealth}→${blobHealth}`) }
  for (const k of SYNC_KEYS) {
    if (k === 'healthData') continue
    const lb = (S[k]||[]).length, bb = (blob[k]||[]).length
    if (lb === 0 && bb > 0) { S[k] = blob[k]; msg.push(`${k} 0→${bb}`) }
  }
  if (!msg.length) { showToast('Nothing to recover — local data is current or larger'); return }
  await save(); render()
  showToast('✅ Recovered: ' + msg.join(', '))
}

async function saveCloudSettings() {
  const rawUrl = document.getElementById('f-cloud-url')?.value?.trim()
  const token  = document.getElementById('f-cloud-token')?.value?.trim()
  const url    = normalizeCloudUrl(rawUrl)
  S.settings.cloudUrl   = url   || ''
  S.settings.cloudToken = token || ''
  S.settings.autoSyncEnabled = document.getElementById('f-autosync')?.checked !== false
  S.settings.autoSyncIntervalMin = parseInt(document.getElementById('f-autosync-interval')?.value) || 0
  await saveSettings()
  startPeriodicPull()   // restart timer with new interval
  render()
  // Show the normalized URL so the user can confirm it looks right
  showToast(url ? `Saved · ${url}` : 'Cloud settings saved')
  // New PWAs begin with no authoritative local database. Once a credential is
  // saved, immediately fetch the committed snapshot instead of showing an
  // empty vault or requiring a hidden recovery action.
  if (url && token && S.settings.autoSyncEnabled && (S._blockAutoPushUntilData || dataRecordCount() === 0)) {
    setTimeout(() => pullOnly(), 50)
  }
}

async function clearCloudSettings() {
  if (!await asyncConfirm('Disconnect from cloud sync?')) return
  S.settings.cloudUrl   = ''
  S.settings.cloudToken = ''
  await saveSettings()
  render()
  showToast('Cloud sync disconnected')
}



function migrateLegacy() {
  // Migrate flat peptides (old format) → new two-level format
  for (const p of S.peptides) {
    if (!p.skus) p.skus = []
    if (!p.dosageProtocols) p.dosageProtocols = []
    if (!p.protocolDocIds) p.protocolDocIds = []
    if (p.halfLife === undefined) p.halfLife = ''
    if (p.absorptionRate === undefined) p.absorptionRate = 0.8
    if (!p.cycleProtocols) p.cycleProtocols = []
    // Peptide-level blend info (for half-life viewer)
    if (p.isBlend === undefined) p.isBlend = false
    if (!p.blendComponents) {
      // Try to migrate blend info up from first blend SKU
      const blendSku = p.skus?.find(s => s.isBlend && s.blendComponents?.length)
      p.blendComponents = blendSku
        ? blendSku.blendComponents.map(c => ({ peptideName: c.peptide || '', peptideId: '' }))
        : []
      if (blendSku) p.isBlend = true
    } // SubQ default ka (hr^-1)
    // Move top-level legacy fields into first SKU
    if (p.barcodes?.length && !p.skus.length) {
      p.skus = p.barcodes.map(b => ({
        id: b.id || uid(),
        barcode: b.code || b.barcode || '',
        vendor: b.source || p.vendor || '',
        lotNumber: p.lotNumber || '',
        purity: p.purity || '',
        concentration: p.concentration || '',
        expiryDate: p.expiryDate || '',
        costPerVial: '',
        coaDocIds: p.coaDocId ? [p.coaDocId] : [],
        isBlend: p.isBlend || false,
        blendComponents: p.blendComponents || [],
        transactions: []
      }))
      if (p.quantity) {
        p.skus[0].transactions = [{ id: uid(), type:'in', qty: +p.quantity, date: now(), notes:'Migrated' }]
      }
    }
    // Migrate legacy fields on existing SKUs
    for (const s of p.skus) {
      if (!s.transactions) s.transactions = []
      if (!s.blendComponents) s.blendComponents = []
      // v7.1+: peptide placement lives only in vaultAssignments. The shared
      // storageLocations collection is retained exclusively for supplies.
      s.locations = []
      // Migrate single coaDocId → coaDocIds array
      if (!s.coaDocIds) {
        s.coaDocIds = s.coaDocId ? [s.coaDocId] : []
        delete s.coaDocId
      }
      if (s.quantity !== undefined && !s.transactions.length) {
        s.transactions = [{ id: uid(), type:'in', qty: +s.quantity, date: now(), notes:'Migrated' }]
        delete s.quantity
      }
    }
    // Clean legacy top-level fields (keep isBlend and blendComponents — used for half-life viewer)
    delete p.barcodes; delete p.vendor; delete p.purity; delete p.concentration
    delete p.expiryDate; delete p.coaDocId
    delete p.quantity; delete p.lotNumber
    // v3.0: new peptide fields
    if (!p.customVialName) p.customVialName = ''
  }

  // Preserve SKU-level document access while also establishing the COA catalog.
  reconcileCoaDocumentLinks(S, uid, now)

  // v3.0: seed default users if none exist
  if (!S.users || !S.users.length) {
    S.users = [
      { id: 'todd',   name: 'Todd',   active: true, color: '#0072B2', initials: 'TO', notes: '' },
      { id: 'meagan', name: 'Meagan', active: true, color: '#CC79A7', initials: 'ME', notes: '' },
    ]
  }
  // v3.0: seed peptide library if empty
  if (!S.peptideLibrary || !S.peptideLibrary.length) {
    S.peptideLibrary = PEPTIDE_LIBRARY_SEED.map(p => ({ ...p, id: 'lib_' + uid() }))
  }
  // v3.0: ensure new arrays
  if (!S.changeLog)  S.changeLog  = []
  if (!S.customCategories) S.customCategories = []
  if (!S.refillLog) S.refillLog = []
  // Safety guards — ensure every persistent array exists (protects against partial imports/cloud blobs)
  if (!S.peptides)         S.peptides = []
  if (!S.documents)        S.documents = []
  if (!S.storageLocations) S.storageLocations = []
  if (!S.injections)       S.injections = []
  if (!S.healthData)       S.healthData = []
  if (!S.notes)            S.notes = []
  if (!S.labResults)       S.labResults = []
  if (!S.supplements)      S.supplements = []
  // v3.2: migrate categories from library entries onto matching inventory peptides (one-time)
  for (const p of (S.peptides||[])) {
    if (p.categories === undefined) {
      // Pull categories from a matching library entry if present
      const lib = (S.peptideLibrary||[]).find(e => normalizePeptideName(e.scientificName) === normalizePeptideName(p.name))
      p.categories = (lib && lib.categories) ? [...lib.categories] : []
    }
  }
  // v7.7.5: canonical category rename across inventory and library records.
  const CATEGORY_RENAMES = { 'Longevity / Cellular Health':'Cellular / Longevity' }
  for (const p of (S.peptides||[])) p.categories = (p.categories||[]).map(c => CATEGORY_RENAMES[c] || c)
  for (const entry of (S.peptideLibrary||[])) entry.categories = (entry.categories||[]).map(c => CATEGORY_RENAMES[c] || c)
  for (const category of (S.customCategories||[])) {
    if (CATEGORY_RENAMES[category.label]) category.label = CATEGORY_RENAMES[category.label]
    if (CATEGORY_RENAMES[category.originalLabel]) category.originalLabel = CATEGORY_RENAMES[category.originalLabel]
  }
  // v3.2: migrate users from old colors to Okabe-Ito colorblind-safe palette (one-time)
  const COLOR_MIGRATION = { '#4f8cff':'#0072B2', '#9b59b6':'#CC79A7', '#007aff':'#0072B2', '#34c759':'#009E73', '#af52de':'#CC79A7', '#2ecc71':'#009E73' }
  for (const u of (S.users || [])) {
    if (u.color && COLOR_MIGRATION[u.color.toLowerCase()]) u.color = COLOR_MIGRATION[u.color.toLowerCase()]
  }
  // v6.5: normalize auto-generated Master Plan episode names to "Round N".
  // Only rename names that match prior importer-generated patterns; preserve custom user names.
  var importedGroups = {}
  for (const p of (S.protocols || [])) {
    if (p.sourceType !== 'excel-master-plan' || !p.sourceColumnName) continue
    var gk = (p.sourceWorkbookFingerprint || '') + '|' + p.sourceColumnName
    if (!importedGroups[gk]) importedGroups[gk] = []
    importedGroups[gk].push(p)
  }
  Object.keys(importedGroups).forEach(function(gk) {
    var group = importedGroups[gk]
    if (group.length < 2) return
    group.sort(function(a,b) { return String(a.startDate || '').localeCompare(String(b.startDate || '')) })
    group.forEach(function(p, idx) {
      var base = p.sourceColumnName
      var legacy = idx === 0 ? base : base + ' (' + (idx + 1) + ')'
      var roundName = base + ' — Round ' + (idx + 1)
      if (p.name === base || p.name === legacy || /^(.+) — Round \d+$/.test(p.name || '')) p.name = roundName
    })
  })

  // v3.0: supplement dosage override fields
  for (const s of (S.supplements || [])) {
    if (s.dosagesDifferent === undefined) s.dosagesDifferent = false
    if (!s.userDosages) s.userDosages = {}
  }
}

// Schema migration registry lives in services/schema-migrations.js (v6.5).

// ══════════════════════════════════════════════════════
// BN30: Backup Validation + Selective Restore
// ══════════════════════════════════════════════════════
function validateBackup(jsonString) {
  try {
    var data = runSchemaMigrations(typeof jsonString === 'string' ? JSON.parse(jsonString) : jsonString)
    var issues = []
    var critical = ['peptides', 'supplements', 'users']
    critical.forEach(function(k) { if (!Array.isArray(data[k])) issues.push('Missing: ' + k) })
    var sv = data._schemaVersion || 0
    if (sv > CURRENT_SCHEMA_VERSION) issues.push('Backup schema v' + sv + ' newer than app v' + CURRENT_SCHEMA_VERSION)
    var totalRecords = 0
    SYNC_KEYS.forEach(function(k) { totalRecords += (data[k] || []).length })
    return { valid: issues.length === 0, issues: issues, schemaVersion: sv, totalRecords: totalRecords,
      domains: SYNC_KEYS.filter(function(k) { return (data[k] || []).length > 0 }).map(function(k) { return { key: k, count: (data[k] || []).length } }) }
  } catch (e) { return { valid: false, issues: ['Parse error: ' + e.message], schemaVersion: 0, totalRecords: 0, domains: [] } }
}
function selectiveRestore(jsonString, domainKeys) {
  try {
    var data = runSchemaMigrations(typeof jsonString === 'string' ? JSON.parse(jsonString) : jsonString)
    domainKeys.forEach(function(k) { if (Array.isArray(data[k])) S[k] = data[k] })
    save(); showToast('Restored ' + domainKeys.length + ' domain(s)'); render()
  } catch (e) { showToast('Restore failed: ' + e.message) }
}

export {
  dataDir,
  SEP,
  DATA_FILE,
  initData,
  tryLoadLatestBackup,
  writeLocalBackup,
  manualBackupNow,
  restoreLatestBackup,
  dataRecordCount,
  _lastSaveTime,
  _localDirty,
  getSyncRuntimeStatus,
  syncDeviceId,
  save,
  saveSettings,
  cloudPush,
  _autoPushTimer,
  _pushPending,
  debouncedAutoPush,
  _pullInFlight,
  pullOnly,
  _syncInFlight,
  pushThenPull,
  renderSyncIndicator,
  timeAgo,
  _periodicPullTimer,
  startPeriodicPull,
  normalizeCloudUrl,
  cloudFetch,
  cloudUrlForKey,
  SYNC_KEYS,
  CHUNKED_KEYS,
  yearsInArray,
  pushChunked,
  pullChunked,
  pushPiece,
  pullPiece,
  buildAtomicSnapshot,
  fetchAtomicSnapshot,
  pushAtomicSnapshot,
  applyAtomicSnapshot,
  applyCloudSnapshotSafely,
  fetchSyncConflictSnapshot,
  summarizeSyncDifference,
  mergeSyncRecords,
  reviewSyncConflict,
  resolveSyncDomainConflict,
  restoreThisDeviceFromCloud,
  syncFromCloud,
  syncToCloud,
  recoverFromWholeBlob,
  saveCloudSettings,
  clearCloudSettings,
  migrateLegacy,
  SCHEMA_MIGRATIONS,
  runSchemaMigrations,
  validateBackup,
  selectiveRestore
}
